import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { WorkflowTabs, type WorkflowTab } from '@/components/WorkflowTabs'
import { GeneratorFrame, PriceConfirmDialog, StudioPanel, SubmitBar } from '@/components/studio'
import { Card, CardBody, CardHead, Badge, Button, Chips, CopyButton, Field, Input, Progress, Segmented, Select, Textarea } from '@/components/ui'
import { Icon } from '@/components/icons'
import { SmartImage } from '@/components/SmartImage'
import { fmtTime } from '@/lib/format'
import { RefUploader } from '@/components/RefUploader'
import { VideoAnalysisCard } from '@/components/VideoAnalysisCard'
import { AiFillButton } from '@/components/AiFillButton'
import { ModelSelect } from '@/components/ModelSelect'
import { TemplatePreview } from '@/components/TemplatePreview'
import { ImageLightbox } from '@/components/ImageLightbox'
import { SaveTemplateDialog, type TemplateFormValues, type TemplateShotLine } from '@/components/SaveTemplateDialog'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { DOMESTIC_PLATFORMS, OVERSEAS_PLATFORMS, getPlatform } from '@/config/platforms'
import { buildBoardShotsFillBrief, buildStoryboardPrompt, buildTemplateShotRefineBrief, buildVideoFillBrief, buildVideoPrompt, buildVideoPromptRefineBrief, effectiveStoryboard, storyboardInputKey, videoStoryboard, type VideoInput } from '@/config/prompts'
import {
  DEFAULT_VIDEO_MODE,
  DEFAULT_VIDEO_RESOLUTION,
  STUDIO_VIDEO_MODES,
  STUDIO_VIDEO_SLUGS,
  VIDEO_CAMERAS,
  VIDEO_DURATIONS,
  VIDEO_RATIOS,
  VIDEO_RESOLUTIONS,
  VIDEO_STYLES,
  defaultsOf,
  getStudioVideoMode,
  getVideoMode,
  resolutionLabel,
  type VideoModeId,
} from '@/config/videoModes'
import {
  activeTemplate,
  storyboardWithEditedShots,
  templateShotLine,
  templateTitle,
  templatesForMode,
  type AnyVideoTemplate,
} from '@/config/videoTemplates'
import { useJobCenter } from '@/context/JobCenterContext'
import { useModels } from '@/context/ModelsContext'
import { useVideoTemplates } from '@/context/VideoTemplatesContext'
import { useToast } from '@/context/ToastContext'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/api'
import { FILL_FORM_FIELDS, liveVideoAnalysis, type CustomVideoTemplate, type ProductAsset, type VideoAnalysis } from '@/api/types'
import { ProductAssetPickerButton } from '@/components/ProductAssetPicker'
import { mergeProductRefs, productImportMessage } from '@/lib/productRefs'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { usePricePreview } from '@/hooks/usePricePreview'

type FormState = VideoInput & {
  /**
   * 当前档位。**写进草稿**，所以刷新/切页回来还在同一档；
   * 地址栏的 `?mode=` 优先于它（可分享 / 可回退，见页面顶部说明）。
   */
  mode: VideoModeId
  model: string
  /** 产品参考图（在「引擎与素材 / 参考视频」页）—— 还原产品外观，第一张是主图 */
  refs: string[]
  /**
   * 场景参考图（在「内容与场景」页）—— 控场景 / 氛围 / 构图。
   *
   * **与 `refs` 是两条通道，不是同一份数据的两个入口**：两者要回答的问题不同
   * （`refs` = 这个东西长什么样，`sceneRefs` = 这张片子长什么样），
   * 混成一个数组后用户在素材页传了产品图、到内容页又看到它，会以为传重了。
   * （两页的**序号随档位变**：合并页是第 2、3 位，复刻档是第 1、3 位 ——
   * 所以这里按页签名说，不按数字说，见 `PANE_BODIES` 上面那段。）
   * 提交时两者按「产品图在前、场景图在后」的顺序拼进同一个 `imageUrls`，
   * 并在提示词里逐段写明角色（见 `buildVideoPrompt` 的【参考图用法】）。
   */
  sceneRefs: string[]
  refVideo: string[]
  /**
   * 参考视频的**镜头结构** —— 复刻档第 1 页「分析视频」的产出。
   *
   * **存的是「当前生效的这一版」**：分析回来时写一份，用户在清单里改动就地覆盖。
   * 与分镜提示词同一条思路 —— 页面上只有一个「现在用的版本」，不并排摆
   * 「模型原版 / 手改版」两份（想回到模型原版就再点一次分析）。
   *
   * `videoUrl` 记的是**分析时用的那条片子**：换视频之后这份结构就过期了
   * （判据见 `analysisStale`）。不记的话，换一条新片子、旧结构还静静躺在提示词里，
   * 出片才发现复刻的是上一条的结构 —— 而那时钱已经花了。
   */
  videoAnalysis: VideoAnalysis | null
  camera: string
  platform: string
  /**
   * 清晰度。**必须与所选模型的能力匹配** —— 值取自模型的
   * `capabilities.resolutions`（原样大小写），换模型时由归一 effect 校正。
   */
  resolution: string
  /**
   * 分镜提示词的**手改版**（2026-09-30 按用户要求新增：「分镜提示词出来后能改，
   * 改完点生成分镜图」）。
   *
   * 空串 = 没改过，走 `buildStoryboardPrompt` 的自动版；
   * 非空 = 用户手写的那一份 —— 但它**只在 `sbInputKey` 与当前输入一致时才算数**。
   * 改过产品 / 模板 / 时长之后自动回到自动版，不会像以前那样永久压住自动版。
   *
   * 为什么存进草稿（`useDraft`）而不是页面自己的 state：它是一份**用户输入**，
   * 和产品名 / 卖点同类 —— 刷新要还在、「清空」要一起清掉。
   */
  storyboardPrompt: string
  /**
   * `storyboardPrompt` 与 `shotNotes` 是**为哪一组输入写的**（`storyboardInputKey`）。
   *
   * ## 这一格就是 2026-10-05 那次报障的正解
   *
   * 手改版与镜头细化都是「针对某个产品 + 某个模板」写出来的，却存在**跨产品的
   * 页面级草稿**里。没有这一格时手改版会永久压住自动版 ——
   * 用户原话「为什么这个不会跟着之前的产品的信息改变的？」，
   * 第二次的截图更直白：选了「卡点换装」模板、分镜图还是上一个产品的水壶。
   *
   * ## 为什么是「输入指纹」而不是「草稿版本号」
   *
   * 上午那版草稿迁移（`__draftVersion`）当天就被撤了 —— **版本号是每次写盘都写的**，
   * HMR 保留旧 state 时一次 `persist` 就能把新版本号盖着旧值写下去，
   * 迁移从此永久免检。指纹的判据取自**当前输入**、不依赖任何落盘状态，
   * 所以 HMR / 手改 / 回滚都打不穿。它顺带还自愈了存量：
   * 旧草稿没有这一格（`''`），与任何非空指纹都不等 ⇒ 旧值自动失效、不需要迁移。
   */
  sbInputKey: string
  /**
   * 已经**随「生成视频」用掉**的那张分镜图的 URL。
   *
   * 用户要求原话（2026-10-05）：「分镜图片使用过就清空」。分镜图是**开拍前的核对物**，
   * 随片子交出去之后使命就结束了 —— 留着只会在下一条片子里被当成有效参考图
   * 再次带上（白占一张参考图额度，还会把上一条的构图带进新片子）。
   *
   * 判据取 URL 而不是「用过了」这个布尔：重新生成分镜图得到的是新 URL，
   * 于是新图自动恢复可用，不需要额外重置这一格。
   */
  sbUsedUrl: string
}

/** 与档位无关的默认值。档位相关的那几项由 `defaultsOf()` 给（见 config/videoModes.ts） */
const BASE_DEFAULTS = {
  productName: '',
  sellingPoints: '',
  style: '清爽电商棚拍',
  extra: '',
  model: DEFAULT_FAMILY_ID.video,
  refs: [] as string[],
  sceneRefs: [] as string[],
  refVideo: [] as string[],
  camera: '固定机位',
  resolution: DEFAULT_VIDEO_RESOLUTION,
  /** 空 = 分镜提示词走自动版（见 `FormState.storyboardPrompt`） */
  storyboardPrompt: '',
  /**
   * 镜头细化描述（一行一段）。空 = 用模板自带的分镜。
   *
   * 为什么它是**表单字段**而不是藏在提示词里：AI 补完要看得见、要能改、
   * 要刷新还在 —— 藏在提示词正文里的话，用户只能对着一段长文本猜哪几句是 AI 写的。
   * 与 `storyboardPrompt` 同一类（都是「用户输入」），所以同样进草稿，
   * 也同样受 `sbInputKey` 的绑定约束。
   */
  shotNotes: [] as string[],
  /** 手改版/镜头细化所绑定的输入指纹。空 = 没有手改过任何东西（见 `FormState.sbInputKey`） */
  sbInputKey: '',
  /** 已经随视频生成用掉的分镜图 URL（见 `FormState.sbUsedUrl`） */
  sbUsedUrl: '',
  /**
   * 参考视频的镜头结构（复刻档第 1 页「分析视频」的产出）。
   *
   * `null` = 没分析过 —— 与空数组不是一回事：空数组是「分析过、但一个镜头都没有」，
   * 那种情况提示词里不该出现「参考结构」这一段。
   */
  videoAnalysis: null as VideoAnalysis | null,
}

/**
 * 四个一层页签 + 只在最后一步出现的提交栏。
 *
 * ## 顺序为什么是「参数 → 引擎与素材 → 内容与场景 → 分镜参考图」（2026-09-30 改）
 *
 * 用户原话（附「输出参数」那一屏的截图，指它说应该放第一个 step）：
 * 「参数与生成 应该放第一个 step」。
 *
 * 上一版的顺序是「引擎与素材 → 内容与场景 → 分镜参考图 → 参数与生成」，
 * 病根在于**参数是后面所有产出的输入，却排在最后**：
 * 平台 / 风格 / 清晰度 / 比例 / 运镜 / 时长这六项决定分镜提示词长什么样
 * （见 `buildStoryboardPrompt`），可用户是在第 4 步才改它们 ——
 * 改完回去看第 3 步的分镜图，那张图已经是按旧参数出的了，
 * 而页面不说明这一点，看上去就是「这张分镜图怎么一直在、还跟我的片子不搭」
 * （用户原话「怎么一直在的？」）。参数前置之后，**先定参数、再出提示词、
 * 再出分镜图、最后生成视频**，四个页签正好就是这条流程。
 *
 * ⚠️ 页签顺序一改，`step_${pageKey}_vN` 的版本号必须跟着升（见下面的 `useLocalStorage`）——
 * 老进度里「第 2 页」的语义已经换了，沿用老 key 会让用户静默停在错的页签上。
 *
 * ## ⚠️ 视频复刻档的顺序与上面这条**不一样**（2026-10-08 改）
 *
 * 用户原话两轮：「参考视频（该放）第一位，这里要改一下」→ 后来看到真机截图又补
 * 「上传后应该有分析按钮啊，都说第一部分是分析视频，那些生成引擎跟产品在 step 2」。
 *
 * 上面那条「参数前置」的理由（参数决定分镜提示词长什么样）**只对
 * 带货短视频 / 商品视频档成立**：那一档的输入物是文案与产品图，参考视频
 * 根本不存在，第一个要回答的问题就是「投哪个平台、多长、什么比例」。
 *
 * 复刻档正好相反 —— **必传参考视频**，而且它决定了后面几乎所有参数
 * （复刻它的镜头与节奏，时长也得贴着它选）。所以复刻档排成：
 * **分析视频（`analyze`）→ 引擎与产品（`source`）→ 输出参数（`params`）
 * → 内容与场景（`content`）**。
 *
 * **第 1 页为什么独立成「分析视频」、而不是「引擎与素材里放个上传框」**：
 * 传完片子之后，用户的第一个问题必然是**这条片子是怎么拍的**（几个镜头、多快、
 * 什么景别、什么光线）。这一步的产出（镜头结构清单）会直接进提示词，后面几页
 * 全围着它转。把它塞在素材页里，用户会先被引擎下拉拦住，直到出片才发现
 * 结构根本没对上 —— 而那时候钱已经花了。
 *
 * ⚠️ **两档各有一张顺序表，也各有一张专属页**：
 *   · `storyboard`（分镜参考图）只有合并页有 —— 复刻档没有分镜可排；
 *   · `analyze`（分析视频）只有复刻档有 —— 合并页的参考视频是**可选**的，
 *     给它占一页会让「本来可以不传」的动作看着像必做。
 * 两张表都**只写 id**，标题与描述仍单源取自 `PANES`（枚举单一真源）。
 * 同理，**正文不按下标取，按页签 id 取**（见 `PANE_BODIES`）：
 * 顺序与正文之间的那根线，从「同一个数字」换成「同一个 id」。
 *
 * 其余三条是上一轮定下的、这次没动：
 *
 * - **引擎仍在页签里**（合并页第 2 位、复刻档第 2 位）。它决定参考图要不要传（有图走图生视频、
 *   没图走文生视频，稳定性差一档），也决定单价；放在素材页里，用户不会「先传完图
 *   才发现引擎选错」。它不属于「输出参数」——那六项是**报价与画面规格**，
 *   引擎是**通道**。
 * - **分镜参考图独占最后一个页签**（2026-09-29 新增）。用户原话是
 *   「你选择了模板或者类型，然后输出产品跟要求，第二步输出分镜图（9 宫格），
 *   最后再生成视频」—— 它是一次**独立的产出**（要花一次生图的钱、要等一次出图、
 *   不满意会重出），不是参数里的一行，所以给它单独一屏；「最后再生成视频」
 *   落到**右栏顶部**的提交栏，两者同屏。
 * - 右栏（结果区 + 分镜脚本）**不随页签切换**，它是页面的实时反馈层 ——
 *   改任何一页的参数，分镜与提示词立刻跟着变。
 */
const PANES: WorkflowTab[] = [
  /* ⚠️ 每个条目都必须**写在一行里、且以对象字面量开头**：冒烟是按源码正则
     数「页签对象」的出现次数、再与 `PANE_BODIES` 的键集合比对的（见 smoke-nav）。
     写成多行对象会让它少数一项、报出「声明了 5 个页签却有 4 个键」的假失败。
     同样地，**注释里也别再写一遍那个字面量** —— 正则不剥注释，写一次就多算一个。 */
  { id: 'params', title: '输出参数', desc: '投放平台、视觉风格、清晰度、比例、运镜与时长 —— 这六项既决定成片长什么样，也决定这次花多少钱' },
  { id: 'source', title: '引擎与素材', desc: '先选生成引擎，再传产品参考图或参考视频' },
  { id: 'analyze', title: '分析视频', desc: '先传参考视频（必传），让 AI 拆出它的镜头结构与节奏 —— 后面几页都按这份结构复刻' },
  /* 复刻档专属（2026-10-09 新增）：`source` + `content` **两页合并成这一页**，
     用户原话「2 跟 4 合并吧」。只在 `REPLICATE_PANE_ORDER` 里出现 ——
     合并页仍然是分开的两页（那边引擎与内容各自还有别的东西要说）。 */
  { id: 'engine', title: '引擎与内容', desc: '选引擎、传产品图，再补产品名称与卖点 —— 复刻时靠它替换掉原片里的产品' },
  { id: 'content', title: '内容与场景', desc: '产品名称、视频场景、卖点与场景参考图 —— 决定成片讲什么、画面长什么样' },
  { id: 'storyboard', title: '分镜参考图', desc: '改好分镜提示词，再出一张 3×3 九宫格核对镜头；「生成视频」在最后一步出现，按钮在右栏顶部。视频复刻档没有这一步' },
]

/**
 * **合并页**（带货短视频 / 商品视频）的页签顺序 —— 就是上面那张清单的原序。
 *
 * 单列出来的原因：两档的顺序从 2026-10-08 起分道扬镳，而「合并页用什么顺序」
 * 这件事必须有**一处**能读出来。不写它、让代码里继续用 `PANES` 兜底，
 * 那 `PANES` 就同时背着两个意思（「有哪些页」和「合并页按什么顺序」）——
 * 往后往 `PANES` 里加一张复刻档专属页，合并页会**悄悄多出一个页签**。
 */
const MERGED_PANE_ORDER = ['params', 'source', 'content', 'storyboard'] as const

/**
 * **视频复刻档**的页签顺序。
 *
 * 2026-10-08 第二轮：参考视频独立成第 1 页（`analyze`）。
 * 2026-10-09：`source`（引擎与产品）与 `content`（内容与场景）**合并**成
 * `engine` 一页 —— 用户原话「2 跟 4 合并吧」，页签 4 个 → 3 个。
 *
 * 为什么合并是合理的（而不是单纯少点一次）：那两页在原顺序里**隔着一页**
 * （引擎与产品 → 输出参数 → 内容与场景），用户填完引擎要翻过参数页才能填产品名，
 * 而它们回答的其实是同一个问题 —— **这一条片子拿什么去生成**。
 * 合并后顺序变成「先看懂参考片 → 再定拿什么生成 → 最后定输出规格」，
 * 与「一次只回答一个问题」对齐。
 *
 * 只写 id，标题与描述仍由 `PANES` 单源提供 —— 这里再抄一份文案的话，
 * 改名时必然漏掉一处（铁律：枚举单一真源）。
 *
 * ⚠️ 每一项都必须能在 `PANES` 里找到：写错一个 id，那个页签会**静默消失**
 * （页面照常渲染、点不到报错），所以冒烟按这条钉死，见 smoke-nav 的「复刻档顺序」段。
 */
const REPLICATE_PANE_ORDER = ['analyze', 'engine', 'params'] as const

/**
 * 复刻档下 `source` / `content` 这两个 id **不在页签里了**（合并成 `engine`），
 * 但页面里有多处跨页签文案按 id 点名「去哪一页」（`paneTitle`）。
 * 这里给出折叠关系，让那些文案在复刻档下仍然指得对。
 *
 * ⚠️ 只在复刻档生效：合并页里它们仍是两张独立的页，折过去会指错页
 * （合并页的「引擎与素材」与「内容与场景」是两屏，不是一个地方）。
 */
const REPLICATE_MERGED_INTO: Record<string, string> = { source: 'engine', content: 'engine' }


/*
 * 这里原有两条「分档文案覆盖」：`REPLICATE_SOURCE_COPY` / `REPLICATE_CONTENT_COPY`
 * （复刻档下把「引擎与素材」改叫「引擎与产品」、「内容与场景」的说明去掉「视频场景」）。
 *
 * 2026-10-09 **两条一起删了** —— 不是嫌它们啰嗦，而是**合并之后它们没有承载对象**：
 * 复刻档的页签顺序不再包含 `source` / `content`，那两条覆盖永远匹配不到任何一项。
 * 留着就是「传了也没用」的死分支（铁律：下架能力要连实现与常量一起清），
 * 而且它们的存在会让人以为 `source` / `content` 在复刻档里还会出现。
 * 合并后那两页该叫什么，由 `PANES` 里那条 `engine` 一份说清。
 */

/**
 * 分镜参考图的 app_slug —— **与后端 `registry.APP_KIND` / `taxonomy._BY_SLUG`
 * 里的键逐字一致**（`storyboard`）。
 *
 * 为什么要单独一个 slug、而不是直接借用当前档位的 `short-video`：
 * `app_kind(slug)` 决定这次走哪条链路，档位 slug 会被判成 video → 拿视频模型
 * 去出一张图。所以它必须是一个**独立登记的图片类 slug**（后端已注册为 image）。
 * 反过来说，前端这里改一个字而后端没同步，表现是「任务提交成功、然后失败」，
 * 或者更糟 —— 用视频模型去画分镜图，按秒计费。
 */
const STORYBOARD_SLUG = 'storyboard'

/**
 * 出分镜图用的模型：固定取默认生图模型（duomi 直连，¥0.10/张）。
 *
 * 不给用户选：这一步的产出是**给人看的核对物**，不是交付图，模型换成更贵的
 * 那一档只是多花钱；而且每多一个下拉，分镜页就多一份要解释的东西。
 * 真要换模型，最后一步提交的那支成片才是值得挑引擎的地方。
 */
const STORYBOARD_MODEL = DEFAULT_FAMILY_ID.image

/**
 * 两版分镜提示词是不是**同一版**。
 *
 * 用途：判断手上那张九宫格是不是**按当前提示词**出的（见 `sbFresh`）。
 * 后端把前端上送的 `prompt` 原样落库（`routers/aigc.py` 的 `prompt=prompt`），
 * 所以拿库里那条任务的 `prompt` 与当前提示词逐字比就行 —— 不比较输入字段，
 * 因为**提示词才是那张图的实际输入**：同一组参数只要把提示词改了，出的图就不一样。
 *
 * 首尾空白不算改动（textarea 里多敲一个换行不该让一张图「过期」）。
 * 只比首尾而不是把内部换行也归一 —— 内部换行是用户能看见、能控制的东西，
 * 折行差异会真的改变发出去的文字，判成「同一版」才是撒谎。
 */
const samePrompt = (a: string | undefined, b: string) => (a ?? '').trim() === b.trim()

/**
 * 「没有镜头细化」的**稳定空数组**。
 *
 * 为什么要这个常量而不是就地写 `[]`：`sbNotes` 在未绑定时会退化成空数组，
 * 而 `useMemo(…, [form, tpl, sbNotes])` 里一个每次渲染新建的数组**恒不等于自己** ——
 * 那个 useMemo 就白做了，而它的结果被 `sbPrompt` / `sbFresh` 逐字依赖。
 */
const EMPTY_NOTES: string[] = []

/**
 * 视频模板行 —— 页签**外**的一排可点封面卡（2026-09-29 新增）。
 *
 * 为什么在页签外：它一次铺开的是**五个分属两个页签的参数**
 * （场景在「内容与场景」，风格 / 比例 / 时长 / 运镜在「输出参数」），
 * 塞进任何一个页签都会变成「点完还得自己去找另外几项改没改」。
 * 与档位 chip-row 同属根决策层 —— 先定做什么，再逐页填细节。
 *
 * 卡片为什么必须有图：模板卖的是「出来大概长什么样」。
 * 名字（「会动的服装」）说不清画面，封面一眼就能对上。
 * 封面是**真实摄影**（`scripts/template-assets.json` 用 duomi-image-2 生成），
 * 不是插画 —— 插画给不出「成片质感」这个信息，上一版模板中心的插画封面就栽在这。
 *
 * 播放量角标（92w 这种）是参考站的展示数据，**不是我们的数据**，
 * 只在有值的模板上渲染；商品视频那四条是功能性模板，没有也不硬编一个。
 */
function TemplateRail({
  mode,
  custom,
  current,
  onPick,
  onPreview,
  onSave,
}: {
  mode: VideoModeId
  /**
   * 本账号自存的模板。**只负责传进来** —— 怎么合并、谁排前面、五元组撞车谁赢，
   * 全在 `templatesForMode()` 那一份实现里（页面不写第二份判断）。
   */
  custom: CustomVideoTemplate[]
  /** 当前命中的模板（派生，见 `activeTemplate`）。没有就全部不选中 */
  current?: AnyVideoTemplate
  onPick: (t: AnyVideoTemplate) => void
  /** 卡片上那颗播放按钮 —— 打开分镜预览抽屉（见 `components/TemplatePreview.tsx`） */
  onPreview: (t: AnyVideoTemplate) => void
  /**
   * 尾部那张「存为模板」卡。与分镜参考图页那颗按钮**是同一个入口**
   * （都调页面里的 `openSaveTemplate`）—— 两处各写一遍保存逻辑的话，
   * 一处记住带封面、另一处忘了带，症状是「从这儿存的模板卡片是灰的」。
   */
  onSave: () => void
}) {
  const list = templatesForMode(mode, custom)
  if (!list.length) return null
  return (
    <div style={{ marginBottom: 12 }}>
      <Card>
        <CardHead
          title="视频模板"
          icon="sparkles"
          /* 原来这句话有**两处**：卡头一行 + 轨道下面再一行「模板只铺参数、不锁死 ……」，
             说的是同一件事（点一张铺五个参数、手动改即自定义）。合成一行就够 ——
             那条脚注白占 38px 纵向高度，而这正是用户抱怨的那件事。 */
          hint="一键铺好场景 / 风格 / 运镜 / 时长 / 比例，改动任一项即视为自定义"
          /*
            「存为模板」—— 用户口径（2026-10-08）「用户改完模板可以存储起来」。

            ## 它为什么在卡头右侧，而不是轨道尾部（2026-10-08 当天改过一次）

            第一版是轨道末尾一张与模板卡等大的虚线卡，用户看了直接说「太丑了」。
            两个原因，都不是调间距能救的：

            ① **宽度从来没被约束住**：`.vtpl` 上的 `flex: 1 1 auto` 是给
               `.vtpl-wrap` 内部用的，而那张卡是**轨道的直接 flex 子项**，
               于是它吃掉了轨道全部剩余宽度 —— 实测渲染成一个约 400px 宽的
               巨大空框，而不是设计里那张 84px 的卡。
            ② 就算把宽度钉死，「八张有画面的卡 + 一张空卡」里那个空框**本身**
               就是视觉噪音：它和旁边八张实心封面卡并排，看着像少加载了一张图。

            现在它是卡头右侧一颗普通按钮，与分镜参考图页那颗「存为模板」
            **逐字同一副样子**（`star` + outline）—— 同一个动作在全站只该有
            **一种呈现**，两处长得不一样，用户会以为它们干的是两件事。
            按钮点击也不会误触折叠：`CardHead` 的 `right` 槽位自带事件拦截。
          */
          right={
            <Button
              variant="outline"
              size="sm"
              icon="star"
              onClick={onSave}
              title="把当前这套参数、分镜脚本与全片约束存成模板，下次点一下就能铺开"
            >
              存为模板
            </Button>
          }
        />
        <CardBody>
          <div className="vtpl-rail">
            {list.map((t) => {
              const on = current?.id === t.id
              return (
                /* 外壳管两件事：定宽（轨道里只有它是 flex 子项），以及给播放按钮当定位基准 */
                <div className="vtpl-wrap" key={t.id}>
                  <button
                    type="button"
                    className="vtpl"
                    data-active={on}
                    /*
                      `data-custom` —— 「这条是自存模板」，**纯元数据，不参与任何样式或行为**。
                      加它是因为：轨道是「自存在前」，于是**第 1 张卡是谁取决于用户存没存过模板**。
                      `verify-templates.py` 原先按下标 `[0..7]` 取「那 8 条内置模板」，
                      有自存模板之后就一直取错（把自存那条当成内置第一条，还漏测了最后一条内置）。
                      现在脚本按这个属性把两者分开，不再依赖顺序。
                      ⚠️ 取值用 `|| undefined`：`data-custom={false}` 会渲染成 `data-custom="false"`，
                      属性**照样在**，选择器 `[data-custom]` 就把内置模板也框进来了。
                    */
                    data-custom={t.custom || undefined}
                    onClick={() => onPick(t)}
                    title={`${t.desc}\n\n${t.preset.scene} · ${t.preset.style} · ${t.preset.camera} · ${t.preset.duration}s · ${t.preset.aspectRatio}`}
                  >
                    <span className="vtpl__cover">
                      <SmartImage src={t.cover} alt={templateTitle(t)} />
                      {t.views ? (
                        <span className="vtpl__views">
                          <Icon name="play" size={10} />
                          {t.views}
                        </span>
                      ) : null}
                      {on ? (
                        <span className="vtpl__tick">
                          <Icon name="check" size={12} strokeWidth={2.6} />
                        </span>
                      ) : null}
                    </span>
                    <span className="vtpl__name">{templateTitle(t)}</span>
                  </button>
                  {/*
                    预览播放按钮（2026-10-05 用户要求）。**不能塞进上面那颗 button** ——
                    button 套 button 是非法 HTML，浏览器会把它拆出去。所以做成兄弟节点、
                    绝对定位浮在封面上。只有正中那颗圆吃点击：点封面其余地方仍是选中模板。

                    文案说「会拍成什么样」而不是「分镜示例」—— 抽屉里放的是**真案例片**
                    （`public/templates/<id>.mp4`，有就播），没出片的模板才回落逐格播，
                    两种都是"这条模板会拍成什么样"，而"分镜示例"只覆盖了后者。
                  */}
                  <button
                    type="button"
                    className="vtpl__play"
                    onClick={() => onPreview(t)}
                    title="预览这条模板会拍成什么样"
                    aria-label={`预览 ${templateTitle(t)}`}
                  >
                    <span>
                      {/* 播放键用实心三角（Icon 默认是描边，这里按需覆盖）*/}
                      <Icon name="play" size={11} fill="currentColor" strokeWidth={0} />
                    </span>
                  </button>
                </div>
              )
            })}
            {/*
              ⚠️ 这里原来还有一张「存为模板」卡（`.vtpl--new`，2026-10-08 当天删）。
              它已经挪去卡头右侧，**别再搬回轨道里** —— 原因两条都写在上面
              `CardHead right` 的注释里（宽度漏气 + 空框本身就是噪音）。
              它与分镜参考图页那颗按钮**仍然是同一个入口**（同一个 `onSave`
              → 页面里的 `openSaveTemplate`）：两处各写一遍保存逻辑的话，
              一处记住带封面、另一处忘了带，症状是「从这儿存的模板卡片是灰的」。
            */}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

/**
 * 视频工作台（合并页，视频复刻仍独立）。
 *
 * ## 合并了两轮（2026-09-29 合路由，2026-10-05 合档位）
 *
 * **第一轮（2026-09-29）**，用户原话「带货短视频跟商品视频合并」：
 * 合并前侧栏「视频」组有三条入口，其中两条是**同一件事** ——
 * 带货短视频（`/short-video`）与商品视频（`/main-video`）共用同一条操作路径
 * （引擎 → 素材 → 内容与场景 → 参数 → 提交），差别只有产出形态
 * （15 秒竖版带货 vs 8 秒方版展示）。那一轮合掉的是**路由**：
 * 两条侧栏入口与两份 `jobsFor` 键收敛成一条 `/video-studio`，
 * 档位在页外用 chip-row 切。与「主图 / 套图 / 详情」三页合一同一个动作。
 *
 * **第二轮（2026-10-05）**，用户看过一轮之后的判词是「这两个感觉差不多啊，
 * 需要合并」：chip-row 那排选择器也没必要留 —— 对照当时的模板清单他确实说对了，
 * 商品视频档的 `mv-01/03/04` 与短视频档的 `vt-08/vt-10` 是同一类拍法，
 * 两个 chip 切来切去看到的是差不多的东西。所以这一轮**连档位一起合掉**：
 * `config/videoModes.ts` 里删掉 `main-video` 条目，chip-row 因此不再渲染，
 * 模板并成一条轨道（重复的三条已删）。旧链接靠**回落**兼容，不是靠留僵尸档位 ——
 * `?mode=main-video` / 老书签 `/main-video` 进来自动落到主力档。
 *
 * 档位清单的唯一真源是 `config/videoModes.ts`（页面里不写第二份）。
 *
 * **视频复刻（`/video-replicate`）不并入** —— 用户原话「视频复刻不变」。
 * 它的输入物性质相反：**必传参考视频**，是复刻既有结构而不是从文案生成，
 * 所以仍是独立入口、独立路由、独立草稿与独立任务键（`video-replicate`）。
 * 它在模式清单里用 `standalone` 标出来。
 *
 * ## 结构迭代史（为什么现在是 3 个页签）
 *
 * **第一版 · 页签**：WorkflowTabs 两个页签（素材 / 参数）。
 * **第二版 · 扁平一屏**（2026-09-28 上轮）：用户驳回第一版，原话「视频平台改为
 * 这些简单，现在太复杂了，右边的结果部分占比过大」，配四张参考站截图。
 * 于是撤掉页签改一屏到底，右栏另在 `.studio` 里收紧（见 global.css 注释）。
 * **第三版 · 3 个页签**（2026-09-28 本轮）：用户反馈「左边的 form 太长了，
 * 操作不友好」。扁平一屏解决了右栏占比，代价是把「引擎卡片 + 参考素材 +
 * 8 个内容参数 + 提交栏」叠成一列，左栏要滚很久才能看到提交按钮。
 * 现在拆成三页，每屏只回答一个问题。
 *
 * **拆页时保留下来的两条结论**（不要因为改了页签数就丢掉）：
 *
 * 1. **引擎是素材页的第一决策**（合并页第 2 位、复刻档第 1 位）。它决定参考图要不要传（有图走图生视频、
 *    没图走文生视频，稳定性差一档），也是唯一的「用哪个通道/什么单价」。
   *    ⚠️ 2026-09-30 页签重排后「第 1 页」是输出参数（见 PANES 的头注释）；
   *    2026-10-08 复刻档又反过来把「参考视频」放到第 1 位 —— 这条结论保留的是
   *    **引擎与素材同页**，**不是**「引擎必须是第 1 页」：两档的引擎都还在这张卡里，
   *    位置随档位变，能力与单价一字未动。
 * 2. **提交栏常驻在右栏顶部，不随页签切换消失**（2026-10-08 起与全站一致）。
 *    撤掉分步向导的核心原因就是向导把「可提交状态」藏起来了 —— 走完前面几步
 *    才知道要花多少钱、按钮在哪里。
 *    ⚠️ 2026-09-30 页签重排后这条只剩前半句成立：报价与按钮**只在最后一个页签
 *    才出现**（用户口径，见下面 submitDock 的注释）—— 页签可以切，但报价不常驻。
 *
 * 另：引擎选择是**一行下拉**（2026-09-29 由卡片改回，见 `stepSource` 的注释）——
 * 单价写在选项文本里，通道看字段右侧徽标，不必再用一屏卡片去换这点信息。
 *
 * ## 两个被修掉的隐形问题
 *
 * - **「投放平台」以前根本不存在**：`buildVideoPrompt` 一直支持 `platform`，
 *   但调用方从没传过 → 提示词里的「投放 淘宝天猫」和【平台风格】永远是默认值，
 *   字段形同虚设。现在它是表单第一项，平台的风格关键词与通版尺寸都真的进了提示词。
 * - **「运镜」以前是死字段**：控件在、存草稿、提交时带上，但提示词里没用到。
 *   现在接进 `buildVideoPrompt`，选了什么就真的写进镜头要求。
 */
export function VideoStudio() {
  const { pathname } = useLocation()
  const [sp, setSp] = useSearchParams()

  /**
   * 视频复刻是**独立页**（用户原话「视频复刻不变」），模式由路径决定。
   * 合并页则相反：模式来自地址栏 `?mode=`（可分享 / 可回退），其次草稿。
   *
   * `pageKey` 把「一个页面」的两件事钉在一起：草稿键、任务键（读 `jobsFor` 与
   * 写 `sourcePage` 必须是同一个值，否则右栏恒「共 0 个」且本页历史整卡不渲染）。
   */
  const isReplicate = pathname.includes('video-replicate')
  const pageKey = isReplicate ? 'video-replicate' : 'video-studio'
  const initialMode: VideoModeId = isReplicate ? 'video-replicate' : DEFAULT_VIDEO_MODE

  const { value: form, patch } = useDraft(pageKey, {
    ...BASE_DEFAULTS,
    ...defaultsOf(initialMode),
  })
  /**
   * 页签进度按**页面**存一份（合并前是按档位三份：切档等于换页，进度归零）。
   *
   * ⚠️ **key 必须带版本，且每次页签顺序/数量变了都要升**：
   * v1 → v2 是页签 3 个变 4 个（第 3 位换人）；
   * v2 → v3（2026-09-30）是**顺序重排**（「输出参数」从第 4 位提到第 1 位）；
   * v3 → v4（2026-10-08 上午）是**复刻档顺序重排**（复刻档第 1 位由「输出参数」
   * 换成「参考视频」）；
   * v4 → v5（2026-10-08 下午）是**复刻档多了一页** —— 参考视频从「引擎与素材」
   * 里独立出来成了第 1 页「分析视频」（3 页 → 4 页）。合并页页签没动，
   * 但两档共用这一个 key，一起升。
   *
   * 沿用老 key 的话，上一轮停在复刻档「引擎与素材」的用户进来会落在第 2 页
   * （现在是「引擎与产品」）—— 页面正常渲染、只是停错了页，截图上完全看不出来；
   * 更要命的是老 `step=3` 在**只有 4 页**的新顺序里会落到「内容与场景」，
   * 而用户以为自己还在参数页。
   * （这类错位本项目已经踩过一次，见 smoke-nav 里 img-localize 那段。）
   */
  const [step, setStep] = useLocalStorage<number>(`step_${pageKey}_v5`, 0)
  const { cancel, jobsFor, outputsFor, clearResults } = useJobCenter()
  const flow = useGenerateFlow()
  const toast = useToast()
  /* 存完模板后「去模板中心」用它 —— 模板墙在首页（见 App.tsx 的路由注释），不新开页签 */
  const navigate = useNavigate()
  /**
   * 本账号自存的视频模板（用户口径：「用户改完模板可以存储起来」）。
   *
   * 只做三件事，别的都不碰：把它传给模板条与 `activeTemplate()`、
   * 在保存成功后刷新 —— 合并规则、排序、深浅拷贝都在 `config/videoTemplates.ts`
   * 与 Context 里，页面不写第二份判断。
   *
   * `tplReady` 只有一个用处：`?vtpl=` 深链要等它。自存模板是异步拉的，
   * 挂载那一刻必然还不在清单里，不等待就会被判成「这个 id 不存在」——
   * 用户从模板中心点自己的模板进来，看到的是「点了没反应」（且地址栏的
   * `vtpl` 已被删掉，刷新也回不来）。
   */
  const { items: customTpls, ready: tplReady, create: createVideoTpl } = useVideoTemplates()
  /* 「AI 细化镜头」是按次计费的（后端真扣积分），成功后要刷顶栏余额 */
  const { refresh: refreshBalance } = useAuth()

  /** 当前档位：合并页认地址栏，认不出（或被手改成别的档）就沿用地里的草稿 */
  const mode: VideoModeId = isReplicate
    ? 'video-replicate'
    : getStudioVideoMode(sp.get('mode') ?? form.mode).id
  const meta = getVideoMode(mode)

  /**
   * 页签清单 —— 本页页签的**唯一真源**：页签栏、正文取用、提交栏判「是否最后一页」、
   * 跨页签文案点名，全都从这一份派生，页面里不许再算第二次「复刻档少一个」。
   *
   * 两档**各按自己那张顺序表**取：
   *   合并页 = `MERGED_PANE_ORDER`（输出参数 → 引擎与素材 → 内容与场景 → 分镜参考图）
   *   复刻档 = `REPLICATE_PANE_ORDER`（分析视频 → 引擎与内容 → 输出参数）
   * 两张表**都是显式的**（合并页那份不再靠「`PANES` 原序」兜底）—— 理由见
   * `MERGED_PANE_ORDER` 的注释：让 `PANES` 同时背「有哪些页」和「合并页什么顺序」
   * 两个意思，往后加一张复刻档专属页时合并页会悄悄多出一项。
   *
   * **定义位置被提到各个 `stepXxx` 之前**，因为下面那些块里的跨页签文案要用
   * `paneTitle()` 点名「去哪一页」—— 顺序分档之后，「页签 2」在两个档下指向
   * 不同的页，写死数字就是给自己留一张迟早对不上的地图。
   */
  const tabs = useMemo(() => {
    const order = isReplicate ? REPLICATE_PANE_ORDER : MERGED_PANE_ORDER
    /* 顺序表里只有 id：对象本身仍是 `PANES` 里那一个（改名两档同时生效）。
       找不到的 id 由 `filter` 挡掉 —— 少一个页签，好过整页崩。 */
    return order
      .map((id) => PANES.find((p) => p.id === id))
      .filter((p): p is WorkflowTab => Boolean(p))
  }, [isReplicate])

  /**
   * 按 id 取某页签在**当前档位**下的标题，供跨页签文案使用。
   * 取不到时给空串而不是抛错：文案缺一个词，好过整页白屏。
   *
   * ⚠️ 先过 `REPLICATE_MERGED_INTO`：复刻档下 `source` / `content` 已被合并，
   * 直接查会拿到空串 —— 文案会变成「用在「」页的产品图上」。
   * **缺一个词比缺一句话更难发现**（整句读着还是通的），所以这条折叠必须在。
   */
  const paneTitle = (id: string) => {
    const key = isReplicate ? (REPLICATE_MERGED_INTO[id] ?? id) : id
    return tabs.find((t) => t.id === key)?.title ?? ''
  }

  /**
   * 当前所选模型的**能力卡片** —— 来自后端 registry（值抄自上游 catalog 实测）。
   *
   * 为什么必须拿它、而不是在页面里写死「支持 480/720/1080」：
   * 14 个贰春模型的能力**不一样** —— 只有 3 个同时支持 480/720/1080，
   * MiniMax-H3 只有 768P，运营商渠道那几档只有一个值；
   * 参考视频更是有 4 个模型完全不收。写死一份的话，
   * 用户会在界面上挑到一个该模型根本不支持的档位，提交时才被上游拒。
   */
  const { list: listModels } = useModels()
  const modelInfo = listModels('video').find((m) => m.id === form.model) ?? null
  /**
   * `undefined` = 后端没说（旧的网关模型），回落全局默认；
   * 非空数组 = 后端明确给了范围，**完全以它为准**。
   */
  const allowedRes = modelInfo?.capabilities?.resolutions
  const allowedRatios = modelInfo?.capabilities?.aspect_ratios
  /** 参考图上限：后端没说时沿用旧的 4 张 */
  const refImageMax = modelInfo?.capabilities?.ref_image_max ?? 4
  /** 参考视频上限：`0` 是**明确的不支持**（不是未知），据此把上传框摘掉 */
  const refVideoMax = modelInfo?.capabilities?.ref_video_max ?? 0

  /* ------------------------------------------------------------------ *
   * 从商品库带入（2026-10-09）
   *
   * 用户口径：「其他页面也可以引用这个商品库啊」。这一页带入
   * **产品名 + 卖点 + 产品图**。
   *
   * ⚠️ 这一页的参考图额度是**产品图与场景图共享**的（`refImageMax`，
   * 由模型能力下发），所以带入时的上限不是 `refImageMax`，而是
   * 「扣掉场景图已占之后剩下的那部分」—— 否则带入 4 张产品图会把用户
   * 已经调好的场景图挤出去，而画面上只看到场景图没了，完全联想不到是这一步干的。
   * 场景图优先保留：它是用户明确指定「只学场景与光线」的那一份。
   *
   * ⚠️ 同样不新增第二份 state：带入后就是普通手填值。
   * ------------------------------------------------------------------ */
  const applyProductAsset = (asset: ProductAsset) => {
    const room = Math.max(0, refImageMax - form.sceneRefs.length)
    const incoming = asset.images.map((i) => i.url).filter(Boolean)
    const { urls, dropped } = mergeProductRefs(form.refs, incoming, room)
    patch({ productName: asset.title, sellingPoints: asset.sellingPoints, refs: urls })
    toast.success(productImportMessage(asset.title, { refs: urls.length, dropped, max: room }))
  }

  const resOptions = useMemo(
    () => (allowedRes?.length ? allowedRes : [...VIDEO_RESOLUTIONS]),
    [allowedRes],
  )
  const ratioOptions = useMemo(
    () => (allowedRatios?.length ? allowedRatios : [...VIDEO_RATIOS]),
    [allowedRatios],
  )

  /**
   * 把清晰度 / 比例**校正在模型允许值内**。
   *
   * 为什么会跑偏：草稿里存着上一轮的值，用户换了模型（三档齐全的 Wan 3.0
   * → 只有 768P 的 MiniMax-H3）之后旧值就不合法了。不在这里校正的话，
   * 界面会停在一个没有对应按钮的档位上，一直拖到提交才由上游报错。
   *
   * 优先级：模型支持默认档（1080P）就用它，否则取模型给的第一个 ——
   * catalog 里的顺序是按档位从高到低排的，第一个即该模型能给的最高档。
   */
  useEffect(() => {
    if (resOptions.length && !resOptions.includes(form.resolution)) {
      patch({
        resolution: resOptions.includes(DEFAULT_VIDEO_RESOLUTION)
          ? DEFAULT_VIDEO_RESOLUTION
          : resOptions[0],
      })
    }
    if (ratioOptions.length && !ratioOptions.includes(form.aspectRatio)) {
      patch({ aspectRatio: ratioOptions.includes(meta.ratio) ? meta.ratio : ratioOptions[0] })
    }
  }, [form.resolution, form.aspectRatio, resOptions, ratioOptions, meta.ratio, patch])

  /**
   * 换到**不收参考视频**的模型时，清掉已传的参考视频。
   *
   * 不清就是哑失败：界面上明明挂着一条视频，提交时上游直接忽略（它根本没这个入参），
   * 用户拿到一个「没参考」的成片，还以为模型不听话。
   *
   * 复刻档除外 —— 那一档参考视频是**必传输入**，换了个不收视频的模型应当由
   * `ready` 拦住不让提交（提示用户换个模型），而不是把用户传好的东西删掉。
   */
  useEffect(() => {
    if (meta.needVideo || refVideoMax > 0 || !form.refVideo.length) return
    patch({ refVideo: [] })
  }, [meta.needVideo, refVideoMax, form.refVideo.length, patch])

  /** 写地址栏但**不碰另一个参数** —— mode 与 scene 各改各的，互不覆盖 */
  const writeParam = (k: string, v: string) =>
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set(k, v)
        return next
      },
      { replace: true },
    )

  /**
   * 落地对齐（只在挂载跑一次）。三件事，都是为了消灭「看着像坏了」的状态：
   *
   * ① **地址栏点名了另一档**（老书签 `/main-video`、模板卡片、分享链接进来）：
   *    按那一档的默认值**整组**铺一遍。只换场景不换时长就会出现
   *    「模式是商品视频、时长却是带货短视频的 15 秒」这种混搭，而且时长按秒计价，
   *    报价会跟着串档。
   * ② **场景不在本档清单里**：回落本档第一个场景。场景是 chips 控件，
   *    值不在列表里会一个都不高亮 —— 用户看到的是「场景没选」。
   * ③ **`?vtpl=` 点名了一个模板**（模板中心「短视频爆款」卡片进来的）：
   *    按模板铺参数。铺完**立刻把 vtpl 从地址栏摘掉** —— 它和 `mode` / `scene`
   *    不是一类参数：后两者是「用户当前状态」，刷新要还原；vtpl 是**一次性指令**，
   *    留下来就会与用户的后续改动打架（用户改完时长再刷新，会被模板按回去，
   *    改动静默丢失）。摘掉之后刷新走草稿，用户改的还在。
   */
  useEffect(() => {
    const m = getVideoMode(mode)
    const next: Partial<FormState> = {}
    if (sp.get('mode') && sp.get('mode') !== form.mode) Object.assign(next, defaultsOf(m.id))
    const wantScene = sp.get('scene')
    if (wantScene && m.scenes.includes(wantScene)) next.scene = wantScene
    else if (!m.scenes.includes(form.scene)) next.scene = m.scenes[0]

    const wantTpl = sp.get('vtpl')
    /* 🔴 **自存模板的深链不在这里消费** —— 它是异步拉的，挂载这一刻清单必然还是空的。
       按「找不到」处理就会把地址栏的 vtpl 删掉，用户从模板中心点自己的模板进来
       看到的是「点了没反应」，而且刷新也回不来（指令已经被自己吃掉了）。
       判据用 id 前缀（`vmt_` = 自存，见 db 的 `new_id("vmt_")`）：
       它同时是来源标记与「要不要等」的判据，不必再读一次 Context。 */
    if (wantTpl && !wantTpl.startsWith('vmt_')) {
      const t = templatesForMode(m.id).find((x) => x.id === wantTpl)
      if (t) Object.assign(next, t.preset)
      setSp(
        (prev) => {
          const p = new URLSearchParams(prev)
          p.delete('vtpl')
          return p
        },
        { replace: true },
      )
    }

    if (Object.keys(next).length) patch(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * `?vtpl=vmt_xxx` —— **自存模板**的深链（从模板中心「我的模板」点进来的）。
   *
   * 与上一条的 ③ 是同一件事，只是**不能同批做**：自存模板在 Context 里异步拉，
   * 挂载那一刻清单必然是空的。所以这里等 `tplReady` 再消费，消费完同样立刻
   * 把 `vtpl` 从地址栏摘掉（一次性指令的口径不变）。
   *
   * ⚠️ 判据是 `vmt_` 前缀而不是「清单里有没有」：后者在「用户手改了一个不存在的
   * id」时会永远等下去，地址栏里挂着一个消费不掉的参数。前缀判据的兜底是
   * 「等一次清单就摘掉」—— 最坏情况是这一跳没铺上参数，而不是卡住。
   */
  useEffect(() => {
    const want = sp.get('vtpl')
    if (!want || !want.startsWith('vmt_') || !tplReady) return
    const t = templatesForMode(mode, customTpls).find((x) => x.id === want)
    if (t) patch(t.preset)
    setSp(
      (prev) => {
        const p = new URLSearchParams(prev)
        p.delete('vtpl')
        return p
      },
      { replace: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp, tplReady, customTpls, mode])

  /**
   * 换档位 —— 等于换整条工作流：场景清单、时长、比例、默认平台一起换
   * （见 `defaultsOf` 的注释：这些参数是相互咬合的一套，只换一半就是混搭）。
   * 档位写进地址栏（可分享、可回退）并回到第一个页签。
   */
  const switchMode = (id: VideoModeId) => {
    /* 换档顺带清掉手改过的分镜提示词与镜头细化：换档 = 换整条片子（场景清单都不重叠），
       上一档的分镜词留着只会被当成这一档的输入 —— 那正是「参数与产出对不上」的来源。
       没改过时清空是空操作，不需要额外判断。
       ⚠️ 连 `sbInputKey` / `sbUsedUrl` 一起清：前者的判据是输入指纹、换档后本来就会不等
       （多余但不留脏值），后者指的是上一档那张图 —— 留着会让这一档新出的图被误判成「已用掉」。 */
    patch({ ...defaultsOf(id), storyboardPrompt: '', shotNotes: [], sbInputKey: '', sbUsedUrl: '' })
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('mode', id)
        // 上一档的场景参数对新档位无意义（两档场景清单不重叠），清掉免得残留
        next.delete('scene')
        return next
      },
      { replace: true },
    )
    setStep(0)
  }

  /**
   * `?scene=` 深链直达某个场景（如「模特手持产品」）。
   *
   * 侧栏已压成一层入口（2026-09-28），不再有场景子项；这条深链仍然保留 ——
   * 首页快捷入口、模板卡片、外部分享都可能带过来。
   *
   * URL 与表单双向一致：用户在页面里换场景，URL 跟着改，
   * 刷新回到的还是他刚选的场景（草稿里本来就存着它）。
   */
  const wantScene = sp.get('scene')
  useEffect(() => {
    if (!wantScene || !meta.scenes.includes(wantScene)) return
    if (wantScene !== form.scene) patch({ scene: wantScene })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantScene, mode])

  const pickScene = (v: string) => {
    patch({ scene: v })
    writeParam('scene', v)
  }

  /**
   * 当前命中的模板 —— **从表单反推，不是另存一个 state**。
   *
   * 存 `form.template` 的写法会让「用户改完风格、卡片还亮着」成为一种常态化撒谎；
   * 派生则天然自洽：改了任一字段就自动不再命中。切换档位同理 —— 两档参数不同，
   * 选中态自己就没了，不需要额外的清理分支。
   */
  const tpl = activeTemplate(mode, form, customTpls)

  /**
   * 分镜参考图的任务与产出（最后一个页签）。
   *
   * 它和视频任务是**同一条时间线上的两种 job**：`sourcePage` 都是本页，
   * 靠 `appSlug` 分开（`storyboard` vs 档位 slug）。所以右栏「结果」不会把它
   * 混进来（那边按 slug 集合收窄，见 `jobsFor(pageKey, slugs)`）——
   * 这是刻意的：右栏那栏的标题写着「<档位> 结果」，分镜图是**它的输入**，
   * 混进去会让「本轮生成了几支片子」这个数字变成假的。
   *
   * 取「最近一条」而不是「第一条」：用户重出分镜图时旧的那张还留在列表里，
   * 不排序的话可能一直显示第一次那张（用户会以为重出没生效）。
   */
  const sbJobs = useMemo(
    () => jobsFor(pageKey, STORYBOARD_SLUG).slice().sort((a, b) => b.createdAt - a.createdAt),
    [jobsFor, pageKey],
  )
  const sbRunning = sbJobs.find((j) => j.state === 'queued' || j.state === 'running') ?? null
  const sbDone = sbJobs.find((j) => j.state === 'succeeded' && j.outputs.length) ?? null
  const sbFailed = !sbRunning && !sbDone ? (sbJobs.find((j) => j.state === 'failed') ?? null) : null

  /**
   * 分镜提示词 —— **从当前输入派生 + 允许手改，但手改只在输入没变时算数**。
   *
   * 用户原话是「应该生成提示词，然后生成分镜图，你有思考吗？」——
   * 分镜这一步要的是两个可见的动作：先有提示词、再拿它去出图。
   * 提示词由 `buildStoryboardPrompt` 从**「内容与场景」页 + 「输出参数」页**派生，
   * 用户在这一屏可以直接改。
   *
   * ⚠️ 这违反了「提示词只在提交确认弹窗里展示」那条既有做法（见 `StudioPromptList`
   * 的注释），是**有意为之的一处例外**：别的页的提示词是「发出去才知道长什么样」，
   * 而分镜提示词是**生成前必须先定稿的输入**（用户要边看边调），
   * 放到「提交前那一步」就等于不给改的机会。视频提示词仍然只在那两处出现。
   *
   * ## 手改版与镜头细化**绑定输入指纹**（2026-10-05 修，用户两次报障）
   *
   * 以前是 `sbPrompt = 手改版 || 自动版` —— 手改版一挂上去就**永久压住**自动版，
   * 换产品 / 换模板 / 改时长它都不动。用户先问「为什么这个不会跟着之前的产品的
   * 信息改变的？」，第二次直接给截图：选了「卡点换装」模板、分镜图还是上一个产品的水壶。
   *
   * 现在按「这份东西是为哪组输入写的」判断（`storyboardInputKey`）：
   * 输入一变，手改版与镜头细化**同时失效**，自动版接管。
   * 「同时」是刻意的 —— 只让其中一个失效会出现「提示词是新版的、镜头描述还是
   * 上一个产品的」这种半新半旧状态，比全旧更难判断。
   */
  const sbInputKeyNow = useMemo(
    () => storyboardInputKey({ ...form, storyboard: tpl?.storyboard }),
    [form, tpl],
  )
  /** 手改版/镜头细化是否仍属于当前这组输入。指纹为空 = 从没手改过，一律算不绑定 */
  const sbBound = !!form.sbInputKey && form.sbInputKey === sbInputKeyNow
  const sbCustomPrompt = sbBound ? form.storyboardPrompt.trim() : ''
  const sbNotes = sbBound ? form.shotNotes : EMPTY_NOTES
  /** 手改/细化过、但已因输入变化而失效 —— 界面必须说清，不能悄悄换掉用户写的东西 */
  const sbEditExpired = !sbBound && (!!form.storyboardPrompt.trim() || form.shotNotes.length > 0)
  const sbAutoPrompt = useMemo(
    () => buildStoryboardPrompt({ ...form, storyboard: tpl?.storyboard, shotNotes: sbNotes }),
    [form, tpl, sbNotes],
  )
  const sbPrompt = sbCustomPrompt || sbAutoPrompt

  /**
   * 手上那张九宫格是不是**按当前这版提示词**出的。
   *
   * 为什么要这条判定（2026-09-30 用户报障）：取数只按「本页 + storyboard」，
   * 不看输入 —— 于是**上一轮为别的产品跑的分镜图会一直挂在页面上**，
   * 而且被当成「已作为参考图带上」跟着下一次视频一起提交。
   * 用户原话「怎么一直在的？」，截图里那张九宫格是上一个产品（榨汁杯）的，
   * 与当前表单毫无关系。真发生出去，成片就会照着别人的产品构图。
   *
   * 判据取**提示词**（`samePrompt`），不取事件时间：
   * 用户改完参数再改回来，那张图仍然有效 —— 拿时间戳比会误判成过期，
   * 反而每次都多花一次生图的钱。
   */
  const sbDoneUrl = sbDone?.outputs.find((o) => o.type === 'image')?.url ?? ''
  /**
   * 已经**随「生成视频」用掉**的那张 —— 用过就清空（2026-10-05 用户要求）。
   *
   * 判据取 URL：重新生成分镜图会得到新 URL，新图自动恢复可用，不需要额外重置。
   * 与 `sbFresh` 分开算是为了让两种「不算数」在界面上说不同的话 ——
   * 「参数变了」要提示重出，「用掉了」要说清它去哪了。
   */
  const sbUsed = !!sbDoneUrl && form.sbUsedUrl === sbDoneUrl
  const sbFresh = !!sbDoneUrl && !sbUsed && samePrompt(sbDone?.prompt, sbPrompt)
  /** 当前提示词下可用的分镜图（只有它会被带上、才会渲染成大预览） */
  const sbUrl = sbFresh ? sbDoneUrl : ''
  /** 上一版提示词留下的那张 —— 只在页面底部提一句，不占预览位、不进参考图 */
  const sbStaleUrl = sbFresh || sbUsed ? '' : sbDoneUrl

  /**
   * 分镜提示词折叠区的展开态。
   *
   * 为什么存成 state 而不是交给浏览器：折叠头走 `CardHead`（全站唯一一份折叠实现，
   * `open` 受控），它要求调用方持有状态。默认**收起** —— 出图之后用户看的是图，
   * 提示词属于「要改才点开」的东西。
   */
  const [sbPromptOpen, setSbPromptOpen] = useState(false)

  /** 「AI 细化镜头」进行中（按钮转圈 + 防连点） */
  const [sbShotsBusy, setSbShotsBusy] = useState(false)

  /**
   * 让 AI **只写镜头那一层**，锁区由模板原样拼回（2026-10-04）。
   *
   * 这颗按钮的上一版叫「AI 写一版」，做的是**整份重写提示词**（`api.refinePrompt`）。
   * 它的结构性问题在于：锁区（3×3 等分 / 镜号 1-9 / 九格白缝 / 技术要求）与自由区
   * 混在同一个字符串里，模型重写一遍，那些硬约束就全靠 system 里「保留原有结构与
   * 硬约束」这句**求**它别改。求是概率，不是保证 —— 改写过的提示词看着仍然像那么
   * 回事，改坏了对着截图看不出来。
   *
   * 现在改成按层分工：
   * · **上下文**是「渲染好的骨架 + 一段要你做的」（`buildBoardShotsFillBrief`，
   *   骨架与真正要发出去的那份同源）—— 模型看到「其余都定稿了、就差这几段画面描述」，
   *   比看到一堆要求更知道该写什么；
   * · **返回**只有每段的画面描述（`shots`），锁区由模板拼回，全程不经过模型的手。
   *
   * 段数必须对得上（见下面 `expected`）：换模板、改时长都会变镜头数，错位贴上去
   * 比不贴更糟，所以**对不上一律整体不覆盖**，并把实际段数如实报出来。
   * （2026-10-04 真调实测：不把「数条数不是数格数」说到上下文里，模型会按**格数**
   * 返回 —— 3 段写成 9 项。所以那句提醒同时写在上下文与 system 两处。）
   *
   * 另外三件事与原来一致：
   *
   * ① **它不是一次生成任务**。不建 job、不进任务中心、右栏不会多一张卡 ——
   *    它改的是**输入**，产物就是输入框里那段文字。但它真花钱（后端按条落用量账本），
   *    所以失败要如实报、成功要报花了多少。
   * ② **结果写进 `form.shotNotes`**（镜头层）并顺手清掉 `storyboardPrompt` ——
   *    `sbPrompt = 手改版 || 自动版`，手改版非空时细化出来的镜头根本看不到。
   *    用户点这颗按钮要的就是 AI 这版，所以回到自动版（手改被清在 toast 里说明）。
   * ③ **细化后手上那张九宫格会失效**（`sbFresh` 按提示词逐字比对）。不是 bug ——
   *    提示词变了，那张图就不是这支片子的镜头了。toast 里必须说明，否则用户以为图丢了。
   */
  const doAiShots = async () => {
    if (sbShotsBusy) return
    /* 当前分镜的段数。AI 必须给出同样多的段 —— 不符就整体不覆盖 */
    const expected = videoStoryboard(form.duration, tpl?.storyboard).length
    setSbShotsBusy(true)
    try {
      const res = await api.fillForm({
        kind: 'board-shots',
        /* 骨架用**绑定后**的镜头描述（`sbNotes`）：过期的细化不能让模型照着它再写一版，
           否则「上一组参数的镜头」会借这次细化重新落进新提示词里。 */
        brief: buildBoardShotsFillBrief({ ...form, storyboard: tpl?.storyboard, shotNotes: sbNotes }),
        sourcePage: pageKey,
      })
      /*
       * 拿到响应 = 后端**已经扣过额度**（`/form/fill` 是**按次计费**的：
       * `_bill_text_call` 在解析字段之前就收了钱）。所以刷新放在这里，
       * 而不是放在下面的成功分支 —— 段数对不上时前端会抛错、整体不覆盖，
       * 但那笔钱照样花了，余额必须动，否则用户以为白报了个错。
       */
      void refreshBalance()
      const lines = (res.fields.shots ?? '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
      if (lines.length !== expected) {
        throw new Error(
          `模型返回了 ${lines.length} 段镜头描述，与当前分镜的 ${expected} 段对不上，` +
            `这次没有覆盖你已有的内容；可以再点一次。`,
        )
      }
      const hadCustom = !!sbCustomPrompt
      /* 细化结果**当场绑定当前输入指纹** —— 与手改版同一套规矩：输入一变就失效。
         不写这一格的话，这批镜头描述会在换了产品之后继续生效（见 `sbInputKey` 的注释）。 */
      patch({ shotNotes: lines, storyboardPrompt: '', sbInputKey: sbInputKeyNow })
      toast.success(
        `AI 已细化 ${lines.length} 段镜头（${res.model} · ¥${res.cost.toFixed(2)}` +
          (res.coins > 0 ? ` · 扣 ${res.coins} 积分` : '') +
          ` · ${(res.elapsedMs / 1000).toFixed(1)}s）。` +
          (hadCustom ? '你手改过的那份提示词已清掉，现在按参数自动生成。' : '') +
          `${sbFresh ? '原来那张九宫格已按新提示词失效，重新出一张即可。' : ''}`,
      )
    } catch (e) {
      // 如实报错，且把后端/上游的原话带出来：Key 失效、限流、返回段数不符
      // 三种原因的处理方式完全不同，吞成「失败了」等于让人去猜。
      toast.error(`AI 细化镜头失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSbShotsBusy(false)
    }
  }

  /**
   * **实际上送**的参考图清单：产品图 → 场景图 → 分镜图，按模型额度截断。
   *
   * 为什么要先算这一份、而不是直接拼一个数组发出去：上游对 `input_images`
   * 是**硬截断**（多出来的直接丢，不报错），而提示词里的【参考图用法】是照
   * 张数与顺序逐段写的 —— 截掉了图却不改词，模型就会把「紧随其后的 2 张是场景图」
   * 对到别的图上，成片出现「把分镜图当产品还原」这种说不清来源的错。
   * 所以：**发什么、词里就说什么**，两处都从这一份派生。
   *
   * 顺序是刻意的：产品图必须在前（【参考图用法】第一段就是它）；分镜图排最后，
   * 额度不够时**先丢它** —— 它是镜头结构的参考，而产品外观被丢掉会直接出废片。
   *
   * 分镜图这一项取的是 `sbUrl` —— **只有「按当前提示词出的那张」才在清单里**
   * （见 `sbFresh`）。上一版提示词留下的那张不算数：把它带上，等于让成片照着
   * 别的产品/别的时长排的镜头去拍。
   */
  const refPlan = useMemo(() => {
    const products = form.refs
    const scenes = form.sceneRefs
    const storyboard = sbUrl ? [sbUrl] : []
    const sent = [...products, ...scenes, ...storyboard].slice(0, refImageMax)
    return {
      sent,
      products: products.length,
      scenes: scenes.length,
      /** 分镜图是否真的进了本次上送清单（没进就在分镜页说明，别让用户以为带上了） */
      storyboard: sent.length > products.length + scenes.length,
    }
  }, [form.refs, form.sceneRefs, sbUrl, refImageMax])

  /**
   * 当前挂着的参考视频（**公网绝对地址**）。空串 = 还没传。
   *
   * 只取**第一条**：参考视频的用途是「复刻它的镜头怎么走」，两条片子给出的是
   * 两套互相冲突的镜头结构，模型只能瞎折中。所以分析也只针对这一条。
   *
   * **上传后拿到的就是腾讯云 COS 的公网直链** —— `/api/aigc/upload` 对视频走
   * `cos.upload_bytes`，回来的 `downloadUrl` 就是它（图片才走图床链）。
   * 所以这里**不做任何拼装**：上游 apilio 要能从公网把这条片子拉过去读，
   * 而这个地址本来就是公网可达的。
   *
   * ⚠️ 但**别想当然**：`storage.save_bytes` 那条链路（图片走的那条）回的是站点
   * 相对地址 `/static/...` —— 本机跑的时候 Vite 代理能取到，**公网取不到**。
   * 所以分析卡对「不是 http(s) 开头」的地址会给出明确提示，
   * 而不是发出去干等超时（那种症状看着像「后端卡死」）。
   */
  const analysisVideoUrl = (form.refVideo[0] ?? '').trim()
  /**
   * 真正会进提示词的那份镜头结构 —— **失效判定在提示词这条路上也要做一遍**。
   *
   * 卡片里那个「换片了，请重新分析」只是**给人看的**；提示词这条路才是花钱的那条。
   * 两处共用 `liveVideoAnalysis` 一份实现，所以不会出现「卡片说过期了、提示词却照用」
   * 这种一半对一半错的状态。
   *
   * 定义在这里（而不是在下面 JSX 附近）是**必须的**：`prompt` 的 useMemo 在本次
   * 渲染中就会执行，`const` 在那之前还处于 TDZ，放后面会直接 ReferenceError。
   */
  const analysisForPrompt = useMemo(
    () => liveVideoAnalysis(form.videoAnalysis, analysisVideoUrl),
    [form.videoAnalysis, analysisVideoUrl],
  )
  /**
   * 参考视频拆出来的镜头条数（`0` = 没分析过、或分析结果已过期）。
   *
   * 右栏那张「成片提示词」卡用它说「已拆出 N 个镜头」—— 判据与提示词**同一条**
   * （`analysisForPrompt`），所以卡片说的和真正会进提示词的永远是同一件事。
   * 空行不算数：`shots` 里可能有空白项（模型返回过带空行的清单）。
   */
  const analysisShotCount = (analysisForPrompt?.shots ?? [])
    .map((s) => s.trim())
    .filter(Boolean).length

  /**
   * 提示词。模板的**镜头结构约束**追加进【补充】段（`extra` 在原字段里本来就透传），
   * 模板的**三段式分镜**走 `storyboard` 参数（进【镜头脚本】段，右栏卡片共用同一份）；
   * 参考图的角色（几张产品图、几张场景图、有没有分镜图）也一并告诉提示词 ——
   * 不说明的话，模型会把场景图里的桌面当成要复刻的场景、把产品图里的人当成产品，
   * 更糟的是把九宫格分镜图**连格线一起复刻**（成片变成一格一格的拼贴）。
   *
   * 不在提示词里新开变量：`config/prompts.ts` 与它的 smoke 断言都不用动结构。
   */
  const prompt = useMemo(
    () =>
      buildVideoPrompt({
        ...form,
        /* ⚠️ 必须在 `...form` **之后**覆盖：`form.shotNotes` 可能是上个产品的细化描述，
           而这条提示词与分镜图提示词共用同一份镜头数据（见 `effectiveStoryboard`）。
           漏了这一行就会出现「分镜图跟着产品变了、成片提示词还在讲上一个产品」。 */
        shotNotes: sbNotes,
        storyboard: tpl?.storyboard,
        /* 复刻档的 `scene` 存的是**复刻方式**（结构复刻 / 节奏复刻），不是画面场景 ——
           提示词里那一行的前缀词跟着档走，否则模型会去找一个叫「结构复刻」的场景。
           `scene` 字段本身两个档共用一个（它的值要提交、要进草稿、要落库）。 */
        sceneLabel: isReplicate ? '复刻方式' : undefined,
        refProductCount: form.refs.length,
        refSceneCount: form.sceneRefs.length,
        refStoryboard: refPlan.storyboard,
        /* ⚠️ 参考视频**必须进提示词**，不能只当素材发出去。
           只发素材的话，模型手里有视频、【镜头脚本】却在讲通用三段式
           （`GENERIC_BEATS`），成片就是套模板、跟参考视频没关系。
           复刻档（`/video-replicate`）之所以「必传参考视频」，正是靠这一行才算数。 */
        refVideoCount: form.refVideo.length,
        /* ⚠️ 分析结果只传**没过期的**那一份 —— `analysisForPrompt` 已经替提示词
           做过失效判定（与卡片同一条 `liveVideoAnalysis`）。直接把 `form.videoAnalysis`
           塞进去的话，换了参考视频却没重新分析时，成片会按**上一条片子**的镜头拍 ——
           而界面上的卡片多半显示正常，只有钱花了才发现。 */
        videoAnalysis: analysisForPrompt,
        extra: [form.extra, templateShotLine(tpl)].filter(Boolean).join('\n'),
      }),
    [form, tpl, refPlan.storyboard, sbNotes, analysisForPrompt],
  )

  /** 点模板 = 整组铺参数。地址栏不动 —— 模板是派生量，刷新后表单原样回来，选中态自然还在 */
  const pickTemplate = (t: AnyVideoTemplate) => patch(t.preset)

  /**
   * 正在预览的模板（点卡片上那颗播放按钮进来的）。`null` = 抽屉关着。
   *
   * **预览不铺参数**：它只是让人先看清楚「这条片子会怎么拍」，
   * 点抽屉里的「用这个模板」才真的 `pickTemplate`。两件事分开，
   * 用户翻着看几条也不会把表单搅乱。
   */
  const [previewTpl, setPreviewTpl] = useState<AnyVideoTemplate | null>(null)

  /**
   * 分镜脚本 —— 与提示词的【镜头脚本】是**同一份数据**（`videoStoryboard` 单一真源）。
   * 选中模板时用模板自带的 beats，没选模板退回通用三行。
   */
  /**
   * 分镜脚本 —— 与提示词的【镜头脚本】是**同一份数据**（`effectiveStoryboard` 单一真源）。
   * 选中模板时用模板自带的 beats，没选模板退回通用三行。
   *
   * ⚠️ 以前这里调的是 `videoStoryboard`（只吃模板），而提示词走 `effectiveStoryboard`
   * （还吃镜头细化）—— 于是用户点了「AI 细化镜头」之后，**右栏这张卡与提示词里的
   * 【镜头脚本】是两份不一样的东西**，而卡片下面还写着「与提示词里的【镜头脚本】
   * 是同一份数据」。现在两边都从 `effectiveStoryboard` 出，且共用绑定后的 `sbNotes`。
   */
  const storyboard = useMemo(
    () => effectiveStoryboard(form.duration, tpl?.storyboard, sbNotes),
    [form.duration, tpl, sbNotes],
  )

  /* ---------------- 「AI 生成提示词」（右栏那张卡头上那颗按钮） ---------------- */

  /**
   * AI 汇总出来的那一版成片提示词。`null` = 没生成过（卡片显示分镜脚本）。
   *
   * ⚠️ **它不参与提交**。提交走的仍是 `buildVideoPrompt` 那份（右栏不展示原文，
   * 只在「生成视频」的确认框里逐字看得到）—— 这是本项目的既有约定：
   * 提示词的唯一展示处是确认框（见 `components/studio.tsx` 里那段）。
   * 这里放的是**另一件产物**：AI 按当前参数重写的一版草稿，供查看与复制。
   * 不写回表单、不进草稿、不进指纹 —— 所以它也不会因为参数一变就"过期"，
   * 顶多是与当前参数不同步，用户重新点一次即可。
   */
  const [aiPrompt, setAiPrompt] = useState<string | null>(null)
  const [aiPromptBusy, setAiPromptBusy] = useState(false)

  /**
   * 把前面几页的参数汇总成一版成片提示词（`POST /api/aigc/prompt/refine`，
   * `kind='video-prompt'`）。
   *
   * 与 `doAiShots`（AI 细化镜头）是两件事，别合并：
   * · `doAiShots` 写的是**分镜图提示词**的镜头层，结果落进 `form.shotNotes`，
   *   会真的改变发出去的提示词；
   * · 这一颗只产出**一份草稿**给用户看与复制，一个字段都不写回。
   *
   * 入参与 `buildVideoPrompt` 那次调用**逐项对齐**（同一份 `sbNotes`、同一个
   * `analysisForPrompt`、同一份 extra 拼接）—— 两边参数不一致时，AI 汇总出来的
   * 东西会与真实提示词讲两件事，而用户手上只有一份能看。
   */
  const doAiPrompt = async () => {
    if (aiPromptBusy) return
    setAiPromptBusy(true)
    try {
      const res = await api.refinePrompt({
        kind: 'video-prompt',
        /* 空 = 让模型从 `brief` 从零汇总。**不把模板拼好的提示词传进去**：
           传了就是「在它上面改写」，硬约束（零文字画面等）会过模型的手，
           改坏了对着截图看不出来 —— 这正是「AI 写一版」当初被撤掉的原因
           （见 `doAiShots` 的注释）。只给参数，让它自己组织一版。 */
        prompt: '',
        brief: buildVideoPromptRefineBrief({
          ...form,
          shotNotes: sbNotes,
          storyboard: tpl?.storyboard,
          sceneLabel: isReplicate ? '复刻方式' : undefined,
          refProductCount: form.refs.length,
          refSceneCount: form.sceneRefs.length,
          refStoryboard: refPlan.storyboard,
          refVideoCount: form.refVideo.length,
          /* 与提示词同一条失效判定：过期的分析一个字都不许进去，
             否则 AI 会照着**上一条片子**的镜头汇总 */
          videoAnalysis: analysisForPrompt,
          extra: [form.extra, templateShotLine(tpl)].filter(Boolean).join('\n'),
        }),
        sourcePage: pageKey,
      })
      /* 拿到响应 = 后端已经扣过额度（`/prompt/refine` 与 `/form/fill` 同族，
         在解析之前就收钱）。所以刷新放在这里，而不是成功分支里。 */
      void refreshBalance()
      const text = res.prompt.trim()
      /* 空结果必须报错：「花了钱、提示成功、一个字没出现」是最坏的失败形态 */
      if (!text) throw new Error('模型没有返回任何内容，这次没有生成')
      setAiPrompt(text)
      toast.success(
        `AI 已按当前参数汇总出一版提示词（${res.model} · ¥${res.cost.toFixed(2)}` +
          (res.coins > 0 ? ` · 扣 ${res.coins} 积分` : '') +
          ` · ${(res.elapsedMs / 1000).toFixed(1)}s）。它只是草稿，不会自动提交。`,
      )
    } catch (e) {
      // 如实报错并带出后端原话：Key 失效 / 限流 / 超时三种原因处理方式不同
      toast.error(`AI 生成提示词失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setAiPromptBusy(false)
    }
  }

  const platform = getPlatform(form.platform)

  const price = usePricePreview({
    // app_slug 逐档保留（后端 registry / taxonomy / 报价都认这三个值）——
    // 合并的是页面，不是计价口径
    appSlug: meta.appSlug,
    model: form.model,
    outputs: 1,
    // ⚠️ 必须是**表单里的真实清晰度**。这里以前写死 `'1080p'`，
    // 而贰春通道是按清晰度**分别计价**的（Wan 3.0 的 480P/720P/1080P
    // 分别是 ¥0.158/0.4/0.8 每秒），写死等于给所有档位报同一个价 ——
    // 用户看到的预估与最终扣费差最多 5 倍。
    resolution: form.resolution,
    aspectRatio: form.aspectRatio,
    inputs: { duration: form.duration },
  })

  /**
   * 本页的任务与产出。
   *
   * ⚠️ 2026-10-05 并档后**不能再按 `meta.appSlug` 收窄**：页面上只剩一档，
   * 但库里还躺着当年在「商品视频」档提交的记录（slug = `main-video`）。
   * 按当前档的 slug 过滤，用户切过来会以为历史记录被清空了 ——
   * 而这不是「正常收窄」，是真的少了一批他自己的东西。
   * 所以判据换成 `STUDIO_VIDEO_SLUGS`（合并页历来写过的全部 slug）。
   *
   * 仍要**排除 `storyboard`**：分镜参考图与本页视频任务是同一条时间线上的两种 job，
   * 它是视频的**输入**而不是产出，混进「本轮结果」会让「生成了几支片子」变成假数字
   * （见上面 `sbJobs` 那段）。集合里没有它，所以这里天然被挡住。
   *
   * 复刻档走自己那一支：它的 slug 就是 `video-replicate`，与合并页没有交集 ——
   * 两个页面共用一个组件，`pageKey` 已经把它们分开了，这里只是把 slug 也分开。
   */
  const slugs = isReplicate ? ['video-replicate'] : STUDIO_VIDEO_SLUGS
  const jobs = jobsFor(pageKey, slugs)
  const outputs = outputsFor(pageKey, slugs)
  const needVideo = meta.needVideo

  /**
   * 右栏「本页历史」的两个档（2026-10-09）。
   *
   * 分镜参考图的记录**以前在右栏里一条都看不到**：它按 `STORYBOARD_SLUG` 与成片
   * 分开取数（`sbJobs`，页内那张九宫格卡在用），而右栏只吃 `jobs`（`slugs` 集合里
   * **刻意不含 `storyboard`**）。那条「刻意」仍然成立 —— 它是视频任务的**输入**，
   * 混进「本轮结果」会让「生成了几支片子」变成假数字 —— 所以这里**沿用分档**：
   * `jobs` 一个字没动，只是把 `sbJobs` 一并交给右栏，由用户自己切着看。
   *
   * ⚠️ **只有合并页有分镜这一步**：复刻档没有「分镜参考图」页签（`tabs` 里没有它），
   * 所以那一档不渲染这个 chip —— 否则会出现一个永远空着的档。
   * 判据取 `tabs`（页签清单的真源），不写 `isReplicate`：页签数的沿革那条注释里
   * 反复强调过「判据写 `tabs.length - 1` 而不是 `PANES.length - 1`」同理。
   */
  const historyTabs = [
    { id: 'video', label: meta.name, jobs },
    ...(tabs.some((t) => t.id === 'storyboard')
      ? [{ id: 'storyboard', label: '分镜参考图', jobs: sbJobs }]
      : []),
  ]

  /* ----------------------- 存为模板（用户自存模板） ----------------------- */

  /** 「存为模板」弹窗开着没有。两个入口（模板条尾部 / 分镜页）共用这一个 state */
  const [saveTplOpen, setSaveTplOpen] = useState(false)
  /** 分镜九宫格的放大遮罩（用户口径：「可以点开」） */
  const [sbZoom, setSbZoom] = useState(false)

  /**
   * 存模板时一并记下的**成片样例** —— 模板卡上那颗播放键播的就是它
   * （用户口径「封面是分镜图，内容是生成的视频」）。
   *
   * 取法：本页**最近的、成功的、有视频产出、且比例与本模板一致**的任务里的第一条视频。
   * 不用 `outputs[0]` —— 那个顺序由 Context 决定，而这里要的是「最新」，
   * 靠 `createdAt` 显式排序，换一次 Context 的存储顺序也不会悄悄变成「最旧的」。
   *
   * 🔴 **`aspectRatio` 这一条过滤不能去掉**（2026-10-08 实测踩到）。
   *
   * 原先是「本页最近一条成功的片，不看参数」—— 那时我以为没问题，因为弹窗里
   * 写明了「带上本页**最近**这条成片」。但预览抽屉是**按 `preset.aspectRatio` 设视频框**的
   * （`TemplatePreview` 的 `.tprev__video`），于是「不过滤」的真实后果是：
   *
   * ```
   * 05:11 用 9:16 出了一条片（10s）
   * 06:47 把表单改成 1:1、出一张 1:1 的分镜图（没出片）
   * 06:49 点「存为模板」 → 抓的还是 05:11 那条 9:16
   * ⇒ 抽屉里写着「比例 1:1 / 时长 8s」，画的却是一个方框里夹一条竖片（两侧大片黑边），
   *    播放器读数 0:10。界面自己跟自己打架。
   * ```
   *
   * 这条与项目既有规则是同一件事 —— `verify-templates.py` 顶部那句
   * 「卡片写 1:1，播出来就得是方版，不能悄悄给一条竖版」。那条规则只管内置模板
   * （案例片是拿模板自己的提示词跑的，天然同比例），自存模板的示例片是**用户的历史产出**，
   * 得由这里保证。
   *
   * ⚠️ 只比 `aspectRatio`、不比 scene/style/camera：后三项在画面上**没法程序判定**，
   * 硬比会写成一份谁也不信的等价判断；而比例是一眼看出来的那个，也是既有规则点名的那个。
   *
   * 取不到就留空：模板照存，卡片上的播放键回落到「逐格播分镜」
   * （见 `TemplatePreview` 的两级回落），不会因为「还没出过同比例的片」就不让存。
   * **宁可播分镜，也不要播一条比例不符的片** —— 后者会让用户以为这套参数出的是竖版。
   */
  const sampleVideoUrl = useMemo(() => {
    const latest = jobs
      .filter(
        (j) =>
          j.state === 'succeeded' &&
          /* 认不出比例的旧任务（老数据没有这列）一律不取：宁可没有示例片 */
          !!form.aspectRatio &&
          j.aspectRatio === form.aspectRatio &&
          j.outputs.some((o) => o.type === 'video' && o.url),
      )
      .sort((a, b) => b.createdAt - a.createdAt)[0]
    return latest?.outputs.find((o) => o.type === 'video' && o.url)?.url ?? ''
  }, [jobs, form.aspectRatio])

  /**
   * 打开保存弹窗。两个入口都走它（模板条尾部那张卡 + 分镜页那颗按钮）——
   * 「一次点击只对应一处实现」，两处各写一遍的话，
   * 迟早一处带封面、另一处忘带，症状是「从这儿存的模板卡片是灰的」。
   */
  const openSaveTemplate = () => {
    // 空表单存出来的模板是一套「出厂默认值」，对谁都没用 —— 先拦一道并说清原因。
    // 判据不是「有没有选模板」：手调出来的一整套参数同样值得存（用户口径正是
    // 「用户改完模板可以存储起来」），所以只要产品名或已命中模板任一成立就放行。
    if (!form.productName.trim() && !tpl) {
      toast.error('先填好产品名（或点一张模板），再存成自己的模板 —— 不然存下来的只是一套默认值')
      return
    }
    setSaveTplOpen(true)
  }

  /**
   * 弹窗里「分镜脚本」那块的那颗 **AI 扩写**（2026-10-08 用户口径
   * 「也可以用AI按钮扩写」）。
   *
   * 复用页面上那颗「AI 细化镜头」的同一个后端场景（`kind='board-shots'`）——
   * 它只让模型写**镜头那一层**，锁区（3×3 等分 / 镜号 / 技术要求）由模板原样拼回，
   * 所以扩写出来的东西不会把提示词的骨架改坏。这是刻意不另开一个场景的原因：
   * 同一件事（把每段画面写清楚）有两套 system，改一处必漏一处。
   *
   * ⚠️ **段数对不上一律整体不覆盖**（与 `doAiShots` 同一条）：弹窗里的镜头数
   * 就是这条模板的节奏，模型多给一段少给一段，贴上去比不贴更糟。
   *
   * ⚠️ 上下文里的镜头用**弹窗当前这一版**（`shots` 参数），不是页面上的 `storyboard`：
   * 用户可能刚改过两条再点这颗键，拿旧版去问的话，AI 会照着「改之前」重写，
   * 把他刚打的字冲掉 —— 而他点这颗键要的正是「在我这一版的基础上扩写」。
   */
  const aiExpandShots = async (shots: TemplateShotLine[]): Promise<TemplateShotLine[]> => {
    const res = await api.fillForm({
      kind: 'board-shots',
      brief: buildBoardShotsFillBrief({
        ...form,
        storyboard: tpl?.storyboard,
        shotNotes: shots.map((s) => s.d),
      }),
      sourcePage: pageKey,
    })
    /*
     * 拿到响应 = 后端**已经扣过额度**（`_bill_text_call` 在解析字段之前就收了钱）。
     * 所以刷新放在这里、不放成功分支 —— 下面段数对不上会抛错、整体不覆盖，
     * 但那笔钱照样花了，余额必须动，否则用户以为白报了个错。
     */
    void refreshBalance()
    const lines = (res.fields.shots ?? '')
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
    if (lines.length !== shots.length) {
      throw new Error(
        `模型返回了 ${lines.length} 段镜头描述，与当前 ${shots.length} 段对不上，` +
          `这次没有覆盖你改过的内容；可以再点一次。`,
      )
    }
    toast.success(
      `AI 已扩写 ${lines.length} 段镜头（${res.model} · ¥${res.cost.toFixed(2)}` +
        (res.coins > 0 ? ` · 扣 ${res.coins} 积分` : '') +
        ` · ${(res.elapsedMs / 1000).toFixed(1)}s）。可以直接改，改完再点保存。`,
    )
    return shots.map((s, i) => ({ ...s, d: lines[i] }))
  }

  /**
   * 弹窗里「全片约束」那块的那颗 **AI 扩写**。
   *
   * 走的不是 `fillForm` 而是 `refinePrompt`（`kind='template-shot'`）：
   * 这个场景的产物是**一段正文**（不是结构化字段），而 `fillForm` 那一族
   * 是按 white-list 把键写回表单的 —— 硬塞进去只会多一层没人用的包装。
   * 后端的合法值清单在 `aigc._REFINE_SYSTEM`（唯一真源），这边只是取一个键。
   *
   * 传 `prompt` = 弹窗里当前那段（可能是用户手写的），让它在**这一版基础上**扩写；
   * 留空也能点 —— 后端 brief 里带着整支片子的参数，它会从零写一版。
   */
  const aiExpandShot = async (current: string): Promise<string> => {
    const res = await api.refinePrompt({
      prompt: current,
      brief: buildTemplateShotRefineBrief(
        { ...form, storyboard: tpl?.storyboard, shotNotes: sbNotes },
        form.productName.trim() || (tpl ? templateTitle(tpl) : undefined),
      ),
      kind: 'template-shot',
      sourcePage: pageKey,
    })
    void refreshBalance()
    /*
     * 后端 system 里已明令「绝对不能换行」，但那属于概率，不是保证 ——
     * 而这段文字会被拼进提示词的**一行**（`templateShotLine()`），
     * 多一个换行就把后面的段落结构顶乱。所以收口放在这里（返回给界面之前），
     * 与 `_strip_fence` 挡代码围栏是同一条思路：**不让坏格式走到下游去**。
     */
    const one = res.prompt.replace(/\s*\n+\s*/g, ' ').trim()
    if (!one) throw new Error('模型返回了空内容，这次没有覆盖你已有的约束；可以再点一次。')
    toast.success(
      `AI 已重写全片约束（${res.model} · ¥${res.cost.toFixed(2)}` +
        (res.coins > 0 ? ` · 扣 ${res.coins} 积分` : '') +
        ` · ${(res.elapsedMs / 1000).toFixed(1)}s）。可以直接改，改完再点保存。`,
    )
    return one
  }

  /**
   * 真正落库。**存的是结构，不是拼好的提示词**（用户选的口径）：
   * 五个参数 + 分镜脚本 + 全片约束 —— 下次套用时按**当时那个产品**重新拼词，
   * 所以这条模板换一个产品依然能用。把成品提示词存进去看着更省事，
   * 但那段文字里写着上一个产品的名字与卖点，换产品就废了。
   *
   * ⚠️ 分镜与全片约束取的是**弹窗里交回来的那一份**（`values.shots` / `values.shot`），
   * 不是页面上的 `storyboard` 与 `tpl.shot`：用户可能刚在弹窗里逐条改过
   * （2026-10-08 新加的能力），用页面那份等于把他的改动原地丢掉 ——
   * 而他看着弹窗里的字，绝不会想到「这些改动没生效」。
   *
   * 成功 / 失败的分工：**成功由这里 toast、失败由弹窗自己显示**。
   * 弹窗里再放一份「保存成功」是同一件事说两遍；而失败必须留在弹窗里 ——
   * 用户刚填的名字还在，改一个字就能重试，不用关掉重来一遍。
   * 所以这里**把异常原样抛回去**（吞掉它 = 用户点了保存、弹窗关了、什么都没发生）。
   */
  const saveTemplate = async (values: TemplateFormValues) => {
    /*
     * 失败**不在这里 catch**：异常原样抛回给弹窗，由它显示在框里。
     * 在这里吞掉（或顺手 toast 一条）都是错的 ——
     *   · 吞掉 = 用户点了保存、弹窗关上了、什么都没发生（最坏的一种"沉默失败"）；
     *   · 顺手 toast = 同一条错误同时出现在弹窗内和右上角，用户不知道该看哪。
     * 而弹窗里报错的价值在于：**刚填的名字还在**，改一个字就能重试。
     */
    await createVideoTpl({
      name: values.name,
      tag: values.tag,
      description: values.description,
      modes: [mode],
      preset: {
        scene: form.scene,
        style: form.style,
        camera: form.camera,
        duration: form.duration,
        aspectRatio: form.aspectRatio,
      },
      /* 分镜**按原形态回写**（`storyboardWithEditedShots`）：无条件存 `{shots}`
         会把三段式模板改写成多镜头，而形态是会进提示词的（量词「第 N 段」/「镜头 N」）
         ⇒ 刚点完保存，用户手里那张分镜图就被判成「不新鲜」。详见那个函数的注释。 */
      storyboard: storyboardWithEditedShots(tpl?.storyboard, values.shots),
      shot: values.shot,
      // 封面就是用户已经出好的那张 3×3 九宫格；后端会把它拉回来转存，避免上游直链过期
      coverUrl: sbUrl || '',
      sampleUrl: sampleVideoUrl,
    })
    toast.toast(
      `模板「${values.name}」已存到「我的模板」`,
      'success',
      // 带一颗「去看看」：存完最自然的下一步就是去模板中心确认它长什么样
      // （封面、播放键都在那边）。不带动作的话，用户得自己回想入口在哪。
      { label: '去模板中心', onClick: () => navigate('/') },
    )
  }
  /** 参考素材（产品图 / 场景图 / 参考视频）任一非空，就按图生视频提交 */
  const hasRef = needVideo
    ? form.refVideo.length > 0
    : form.refs.length > 0 || form.sceneRefs.length > 0
  const ready = needVideo ? form.refVideo.length > 0 : form.productName.trim().length > 0

  /**
   * 场景参考图还能传几张 —— **与产品图共享 `ref_image_max` 总额度**。
   *
   * 为什么不各自给满额：上游收的是一个 `input_images` 数组，
   * 两边各放 4 张的结果是提交 8 张、上游直接截断（用户以为自己传上了）。
   * 这里让「内容与场景」页的额度跟着素材页的已用量走，页面上写清剩余张数。
   */
  const sceneRefMax = Math.max(0, refImageMax - form.refs.length)

  /* ---------- 页签 · 分镜参考图（**只在合并页出现**；带动右栏提交栏） ---------- */

  /**
   * 分镜图的报价。
   *
   * `appSlug` 必须是 `storyboard` 而**不是当前档位**：后端按 slug 查产出类型
   * 与价目表，传档位 slug 会被当成视频、按秒报价（¥4.50 而不是 ¥0.10）。
   */
  const sbPrice = usePricePreview({
    appSlug: STORYBOARD_SLUG,
    model: STORYBOARD_MODEL,
    outputs: 1,
    aspectRatio: form.aspectRatio,
  })

  /**
   * 出分镜图。走**同一条报价确认流程**（`flow.request`）——
   * 它同样是一次真实扣费，不该因为便宜就绕过确认；复用同一条路径也意味着
   * 弹窗、扣费口径、失败落卡、右栏任务只有一份实现。
   *
   * **不带任何参考图**（纯文生图）是刻意的：这张图要的是**镜头顺序与构图**，
   * 把产品图也发过去会让模型围着那一张产品图构图，9 个镜头会退化成
   * 「同一张产品定妆图的 9 个角度」—— 那正是分镜图要避免的东西。
   * 产品外观的还原由最后一步那次正式生成负责（那时产品图才进 `imageUrls`）。
   */
  const doStoryboard = () =>
    flow.request({
      info: {
        title: '分镜参考图',
        appSlug: STORYBOARD_SLUG,
        model: STORYBOARD_MODEL,
        outputs: 1,
        aspectRatio: form.aspectRatio,
        refImages: 0,
        prompt: sbPrompt,
        price: sbPrice?.estimatedPrice ?? 0,
        basis: sbPrice?.basis,
        free: sbPrice?.isFreeThisCall,
        platform: platform.name,
      },
      args: {
        appSlug: STORYBOARD_SLUG,
        title: `分镜参考图 · ${form.productName || form.scene}`,
        sourcePage: pageKey,
        model: STORYBOARD_MODEL,
        prompt: sbPrompt,
        inputs: { ...form, __title: '分镜参考图' },
        imageUrls: [],
        aspectRatio: form.aspectRatio,
        outputs: 1,
        estimatedMs: 45000,
      },
    })

  const doGenerate = () =>
    flow.request({
      info: {
        title: meta.name,
        appSlug: meta.appSlug,
        model: form.model,
        outputs: 1,
        aspectRatio: form.aspectRatio,
        resolution: form.resolution,
        refImages:
          form.refs.length + form.refVideo.length + form.sceneRefs.length + (refPlan.storyboard ? 1 : 0),
        prompt,
        price: price?.estimatedPrice ?? 0,
        basis: price?.basis,
        free: price?.isFreeThisCall,
        platform: platform.name,
      },
      args: {
        appSlug: meta.appSlug,
        title: `${meta.name} · ${form.productName || form.scene}`,
        sourcePage: pageKey,
        model: form.model,
        prompt,
        inputs: { ...form, __title: meta.name },
        /*
          ⚠️ 参考图与参考视频**分两个字段发**，不要拼在一起。

          以前这里写的是 `imageUrls: [...form.refs, ...form.refVideo]` ——
          参考视频被塞进图片数组，后端会按图片处理（贰春只认 media_id，
          会把它挂在 input_images 上，而上游看到的是一条视频素材）。
          拆开之后 `videoUrls` 一路走到适配器的 `input_videos`。

          产品图 / 场景图 / 九宫格分镜图**按 `refPlan` 那个顺序拼进同一个
          `imageUrls`**（产品图在前、分镜图在末），并在提示词里逐段写明角色
          （见 `buildVideoPrompt` 的【参考图用法】），这个顺序就是那段描述的锚点。
          **不要再在这里现场拼一次数组** —— 超额度时提示词与实际发出去的图
          会各说各话（`refPlan` 就是为这件事存在的）。
        */
        imageUrls: refPlan.sent,
        videoUrls: form.refVideo,
        aspectRatio: form.aspectRatio,
        resolution: form.resolution,
        outputs: 1,
        estimatedMs: 240000,
      },
    })

  /* -------- 页签 · 参考视频 / 引擎与素材（合并页第 2 位，复刻档第 1 位） -------- */

  const stepSource = (
    <>
      {/*
        引擎 —— 第一决策，但它是**一行下拉**（2026-09-29 按用户要求改）。

        上一版把 14 个模型铺成竖排引擎卡（`ModelSelect variant="cards"`），
        用户原话「下拉框就可以了，现在太占位置」—— 实测那块在 1482px 窗口里
        **1205px 高**，把参考素材卡与提交栏整个顶到折线以下。
        卡片形态已随这次改动**从 ModelSelect 里整块撤掉**（不留第二形态），
        单价没丢：写在每个 option 的文本里，通道徽标与备注在控件下方 hint。

        块级结构也从「CardHead + CardBody」收成「CardBody」—— 标题跟字段名
        「生成引擎」是同一句话说两遍，白占一行。
      */}
      <Card>
        <CardBody>
          <ModelSelect
            kind="video"
            label="生成引擎"
            value={form.model}
            onChange={(v) => patch({ model: v })}
            hasRef={hasRef}
          />
        </CardBody>
      </Card>

      {/*
        参考图与参考视频是**两条并列通道，不是二选一**。

        2026-09-29 之前这里写的是 `kind={needVideo ? 'video' : 'image'}` 配
        `urls={needVideo ? refVideo : refs}` —— 一个档位只能传其中一样。
        后果是带货短视频/商品视频两档**根本没有参考视频入口**，
        而贰春 14 个模型里有 10 个同时收参考图和参考视频：
        参考图控形象、参考视频控运镜与节奏，两者互补而非互斥。

        现在拆成两个上传器，各自按**当前模型的能力**决定显不显示、能传几条。
      */}
      {(!needVideo || refImageMax > 0) && (
        <Card>
          <CardHead
            title="产品参考图"
            icon="image"
            hint={
              needVideo
                ? '可选：补一张产品图，复刻时替换得更准'
                : '可选：传了走图生视频，不上传走文生视频（图生视频效果稳定得多）'
            }
          />
          <CardBody>
            <RefUploader
              wrap="none"
              kind="image"
              urls={form.refs}
              onChange={(urls) => patch({ refs: urls })}
              max={refImageMax}
              dropLabel="拖拽或点击上传产品图"
              dropHint={`支持多选 · 可拖拽排序 · 第一张作为主图${refImageMax ? ` · 本模型最多 ${refImageMax} 张` : ''}`}
              footnote={
                <span>
                  管「产品长什么样」；想控画面氛围
                  {/* 复刻档下两页已合并（`engine`），场景图就在本页下面 —— 说「去某页」会把人指到不存在的页签 */}
                  {isReplicate ? '往下看本页的' : `去「${paneTitle('content')}」页传`}
                  <b>场景参考图</b>
                </span>
              }
            />
          </CardBody>
        </Card>
      )}

    </>
  )

  /* ------- 页签 · 分析视频（**只在复刻档**；它是复刻档的第 1 位） ------- */

  /**
   * 「分析视频」= 传参考视频 + 让 AI 拆出它的镜头结构。
   *
   * 2026-10-08 第二轮按用户要求新增。用户原话：「上传后应该有分析按钮啊，
   * 都说第一部分是分析视频，那些生成引擎跟产品在 step 2」——
   * 也就是把原来的「引擎与素材」一拆为二：参考视频单独成第 1 页，
   * 引擎与产品退到第 2 页。
   *
   * **为什么值得单独占一页**：传完片子之后，用户真正要回答的第一个问题是
   * 「这条片子是怎么拍的」（几个镜头、多快、什么景别）。这个答案会进提示词，
   * 后面几页全围着它转。上一版把它塞在「引擎与素材」里，用户会先被引擎下拉拦住，
   * 直到出片才发现结构没对上 —— 而那时钱已经花了。
   *
   * 所以这一页**不放引擎、不放产品图**：它们跟「看懂这条片子」不是同一件事，
   * 摆在这里只会把上传框挤到折线以下（那正是上一版的样子）。
   *
   * ⚠️ 这里保留 `needVideo || refVideoMax > 0` 这个条件：本页目前只在复刻档
   * 出现（那里 `needVideo` 恒真），但写死「一定渲染」的话，将来某个档把它捡起来
   * 就会出现「让用户传了、上游却忽略」——那是比直接不给更糟的体验。
   */
  const stepAnalyze = (
    <>
      {(needVideo || refVideoMax > 0) && (
        <Card>
          <CardHead
            title="参考视频"
            icon="video"
            hint={
              needVideo
                ? '必传：将复刻它的镜头结构与节奏，换掉产品'
                : `可选：用于复刻运镜与节奏 · ${modelInfo?.name ?? '当前模型'}最多 ${refVideoMax} 条`
            }
          />
          <CardBody>
            <RefUploader
              wrap="none"
              kind="video"
              /* 点缩略图预览时，遮罩标题用这个；不传会落到默认的「参考图预览」 */
              title="参考视频"
              urls={form.refVideo}
              onChange={(urls) => patch({ refVideo: urls })}
              max={needVideo ? 1 : refVideoMax}
              /* 视频**没有「主图」这个语义**（那是产品图的概念）—— 与「场景参考图」
                 同一个理由，见 RefUploader 的 `firstBadge` 说明 */
              firstBadge=""
              dropLabel="拖拽或点击上传参考视频"
              dropHint="MP4 / MOV · 上传后存到腾讯云 COS，公网可直接取回"
            />
          </CardBody>
        </Card>
      )}

      {/*
        镜头结构卡。`videoUrl` 传的是**公网绝对地址** —— 上游 apilio 是从公网把
        这条片子拉过去读的，站点相对路径它取不到；传错的症状不是报错，而是
        「一次十几秒的调用干等满超时」。视频上传走 COS 直链，所以这里不需要做
        任何地址转换（见上面 `analysisVideoUrl` 的说明）。
      */}
      <VideoAnalysisCard
        videoUrl={analysisVideoUrl}
        analysis={form.videoAnalysis}
        onChange={(next) => patch({ videoAnalysis: next })}
        sourcePage={`${pageKey} · 分析视频`}
      />
    </>
  )

  /* ------- 页签 · 内容与场景（两档都有） ------- */

  /**
   * 「内容与场景」= 成片**讲什么**（产品名 / 场景 / 卖点）**+ 长什么样**（场景参考图）。
   *
   * 场景参考图是 2026-09-29 按用户要求新增的（原话「内容与场景的图片上传也要搞」）。
   * 它与素材页的产品参考图**不是同一件事**：
   *   产品参考图 → 还原**这个东西**（颜色 / 材质 / 结构），是「标的物」
   *   场景参考图 → 取**这张片子**的氛围、构图与光线，是「画面风格」
   * 所以分两个字段、两个入口，提交时按「产品图在前、场景图在后」拼进同一个
   * `imageUrls`，并在提示词里逐段写明各是哪张 —— 不写清楚，
   * 模型会把场景图里的人或桌面当成要复刻的东西。
   */
  /**
   * 「AI 填充」不让点的原因（有值 = 禁用，不隐藏）。
   *
   * 前置条件只有**产品名**一条 —— LLM 是纯文本的（看不了参考图），
   * 没有产品名它只能编，而编出来的卖点看着像真的，用户会一路用到出片才发现
   * 产品是它造的（同 `/banner` 那条）。**不禁用「没有卖点」**：那一格本来就是
   * 让 AI 从零补的，空着才是常态。
   */
  const videoFillBlocked = !form.productName.trim()
    ? '先填产品名称 —— AI 需要知道这是什么产品才写得对'
    : undefined

  const stepContent = (
    <Card>
      <CardHead
        title="内容与场景"
        icon="text"
        hint="决定成片讲什么；场景参考图决定画面长什么样"
        right={
          /* 「AI 填充」—— 用户只给产品名，卖点与补充要求由 AI 按本页上下文补齐。
             与最后一个页签那颗「AI 细化镜头」不是一件事：那颗改的是**镜头层**
             （每段拍什么，分镜图提示词与视频提示词共用同一份，改完手上的九宫格
             会失效），这颗改的是**表单字段**（改完提示词自动跟着重算，但那张
             九宫格不会失效 —— 它比的是提示词，而提示词确实变了…所以 toast 里
             要说清「分镜图可能要重出」）。按钮实现与 `/banner` 共用一份。 */
          <AiFillButton<FormState>
            kind="video"
            /* 白名单取自跨前后端的真源 —— 与后端 `_FILL_FIELDS["video"]` 逐字一致，
               键名 `sellingPoints` 就是这一页表单里那一格的字段名（`AiFillButton`
               再挡一道，模型多返回的键进不来）。 */
            fields={FILL_FORM_FIELDS.video}
            brief={buildVideoFillBrief({
              productName: form.productName,
              scene: form.scene,
              /* 与成片提示词同一处分档：复刻档的 scene 是复刻方式，不是画面场景。
                 上下文里写「【视频场景】结构复刻」，模型会去写一个不存在的场景。 */
              sceneLabel: isReplicate ? '复刻方式' : undefined,
              sellingPoints: form.sellingPoints,
              extra: form.extra,
              duration: form.duration,
              aspectRatio: form.aspectRatio,
              style: form.style,
              camera: form.camera,
              platform: form.platform,
              templateName: tpl ? templateTitle(tpl) : undefined,
              refCount: form.refs.length,
              /* 镜头脚本一起给 ——「卖点长度要配得上分镜」这句话得有配的对象
                 （15 秒 6 镜头与 8 秒 3 段，卖点写法完全两样） */
              storyboard: tpl?.storyboard,
              /* 绑定后的镜头描述 —— 与分镜图提示词、成片提示词同源；这里给旧的会
                 让 AI 照着上一个产品的镜头去补卖点 */
              shotNotes: sbNotes,
            })}
            sourcePage={pageKey}
            /* 快照必须在**覆盖前**取 —— 组件里再取就已经是 AI 写过的版本了 */
            snapshot={{ sellingPoints: form.sellingPoints }}
            onFill={patch}
            blocked={videoFillBlocked}
            label="AI 补卖点"
          />
        }
      />
      <CardBody>
        <Field
          label="产品名称"
          required={!needVideo}
          /* 「从商品库选择」—— 商品库录好的档案一次带进来（标题 + 卖点 + 产品图）。
             ⚠️ 图进的是这一页的**产品图**槽位，且受「产品图 + 场景图」共享额度约束
             （见 `applyProductAsset`）。复刻档产品名非必填，但带进来照样有用 ——
             它同时是 AI 补卖点的入口字段。 */
          right={<ProductAssetPickerButton onPick={applyProductAsset} />}
        >
          <Input
            value={form.productName}
            placeholder="如：华数云折叠沥水垫"
            onChange={(e) => patch({ productName: e.target.value })}
          />
        </Field>

        {/*
          用 Chips 而不是 Segmented：场景最多 8 个（带货短视频），
          Segmented 是一行等分的控件，塞 8 个中文标签会各自被压成一列竖排汉字。
          2-4 个选项才用 Segmented。

          ⚠️ **复刻档不渲染这一项**。那一档的 `meta.scenes` 放着的是
          「结构复刻 / 节奏复刻」—— 那是复刻方式，已经提到页签外的根决策区
          （见页面顶部那排）。在这里再渲染一遍就是同一字段两个入口，
          而且标签还写着「视频场景」，指向的根本不是画面场景。
        */}
        {!isReplicate ? (
          <Field label="视频场景">
            <Chips
              value={form.scene}
              onChange={pickScene}
              options={meta.scenes.map((s) => ({ value: s, label: s }))}
            />
          </Field>
        ) : null}

        {/* 卖点与「补充要求」合并成一个输入框 —— 两个自由文本框并列，
            用户并不知道该写进哪个，最后两边各写一半。 */}
        <Field label="卖点与补充要求（可选）" hint="一行一条；画面上的额外要求也写这里">
          <Textarea
            rows={3}
            value={form.sellingPoints}
            placeholder={'150AW 大吸力\n轻至 1.2kg，单手可举\n结尾收在完整产品'}
            onChange={(e) => patch({ sellingPoints: e.target.value })}
          />
        </Field>

        {sceneRefMax > 0 ? (
          <RefUploader
            wrap="none"
            label="场景参考图（可选）"
            fieldHint={`只取场景与光线，不参与产品还原 · 本页还可传 ${sceneRefMax} 张`}
            urls={form.sceneRefs}
            onChange={(sceneRefs) => patch({ sceneRefs })}
            max={sceneRefMax}
            dropLabel="拖拽或点击上传场景参考图"
            dropHint="建议只传 1 张 —— 多张混在一起模型会各取一半"
            /* 场景图没有主副之分（它不参与产品还原），别挂「主图」角标 */
            firstBadge=""
            emptyHint="模型只学它的场景感与光线，不会把里面的人或产品搬进来。"
          />
        ) : (
          <Field label="场景参考图（可选）" hint="本模型的参考图额度已被产品图占满">
            <div className="text-xs muted">
              参考图上限是 {refImageMax} 张，已经全部用在
              {/* 复刻档下产品图与场景图在同一页，指「某页」不存在 */}
              {isReplicate ? '本页上面的' : `「${paneTitle('source')}」页的`}
              产品图上。想加场景图，先移除几张。
            </div>
          </Field>
        )}
      </CardBody>
    </Card>
  )

  /* ------------------------- 页签 · 分镜参考图（仅合并页） ------------------------- */

  /**
   * 状态徽章 —— 折叠头（收起态）与 Field 标签行（展开态）**共用这一份**。
   *
   * ⚠️ 状态**必须由徽章自己说**：出图后这个区是**折叠**的，hint 根本不在屏幕上 ——
   * 而「现在我看到的这份提示词是哪来的」恰恰是折叠态下最该被看见的一件事。
   * 所以「按参数自动 / 我的版本 / AI 细化镜头」三态之外，还要有第四态
   * 「已按当前参数更新」：手改/细化过、但输入已经变了、那份不再生效。
   * 少了这一态，用户会看着一份自己没见过的提示词，以为系统把他的东西弄丢了。
   */
  const sbPromptBadge = (
    <Badge
      tone={sbEditExpired ? 'info' : sbCustomPrompt ? 'neutral' : sbNotes.length ? 'success' : 'neutral'}
    >
      {sbEditExpired
        ? '已按当前参数更新'
        : sbCustomPrompt
          ? '我的版本'
          : sbNotes.length
            ? 'AI 细化镜头'
            : '按参数自动'}
    </Badge>
  )

  /**
   * 分镜提示词输入框 —— **只有一份实现**：出图前（整栏全宽）与出图后（预览下方折叠区）
   * 共用同一份，差别只有行数与标签。写成两份的话，hint 与「恢复自动生成」的禁用判据
   * 会在两个状态里各说各话（本项目「同一控件只许一份实现」的复发点）。
   *
   * `bare` = 出图后把它折进折叠区时用：标签与徽章已经由折叠头承载，
   * 这里就不再重复渲染那一行（同一个字段在屏幕上出现两次标题是噪音）。
   * ⚠️ 动作行**不在这里**渲染（见 `sbPromptActions`）—— 出图后必须留在折叠区外。
   */
  /**
   * 分镜提示词的三颗动作 —— **只有一份实现**，两种排版共用：
   *   · 没出图：跟在输入框下面（由 `sbPromptField` 渲染）；
   *   · 出过图：**常驻在折叠区之外**（由 `stepStoryboard` 直接渲染）。
   *
   * ## 为什么出图后必须搬出来（2026-10-05 用户报障）
   *
   * 用户原话：「为什么视频脚本不可以用 ai 生成多一次？」
   * 能力一直都在 —— 「AI 细化镜头」写的就是视频提示词里那段【镜头脚本】
   * （两条提示词共用 `effectiveStoryboard` 这一份镜头数据）。问题是它跟输入框
   * 一起被折进了「分镜提示词」，而那个折叠区**默认是收起的**：
   * 一旦出过图，用户连按钮的 DOM 都摸不到，自然得出「不能用」的结论。
   *
   * 这条判断本屏早就有了（见下面「按这份提示词重新生成」旁边的注释）：
   * **动作要留在折叠区外常驻 —— 折起来用户就找不到。** 上一版只把
   * 「重新生成」搬了出去，漏了这三颗。
   */
  const sbPromptActions = (
    <div className="row" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
      <Button
        variant="ghost"
        size="sm"
        icon="sparkles"
        loading={sbShotsBusy}
        /* 一次 ¥0.10（后端按条计价并上账）。**刻意不弹价格确认框** ——
           这是「改输入」的轻量动作，为它挡一道弹窗比这一毛钱更烦人；
           花了多少由成功后的 toast 如实报出（含模型名与耗时）。 */
        onClick={doAiShots}
      >
        {sbShotsBusy ? 'AI 正在细化…' : 'AI 细化镜头'}
      </Button>
      <CopyButton text={sbPrompt} label="复制提示词" />
      <Button
        variant="ghost"
        size="sm"
        icon="refresh"
        /* 没改过就没什么可恢复的。**禁用而不是藏起来** —— 让「这里能回退」一直看得见。
           ⚠️ 判据要**同时看 `shotNotes`**：AI 细化的产物存在那里，只清
           `storyboardPrompt` 的话细化出来的镜头会留着，按钮看着像没反应。 */
        disabled={!sbCustomPrompt && !sbNotes.length && !sbEditExpired}
        onClick={() => patch({ storyboardPrompt: '', shotNotes: [], sbInputKey: '' })}
      >
        恢复自动生成
      </Button>
    </div>
  )

  const sbPromptField = (rows: number, bare = false) => (
    <Field
      label={bare ? undefined : '分镜提示词'}
      hint={
        sbEditExpired
          ? '你之前手改 / AI 细化的那一版是按旧参数写的，参数一变换就不适用了 —— 这里已自动切回按当前参数生成。把产品与参数改回原样，还能找回那一版。'
          : sbCustomPrompt
            ? '已改成你的版本：改动上面任何一项产品或参数都会自动切回按参数生成（不会像以前那样一直压着自动版），要立刻回退就点「恢复自动生成」。'
            : sbNotes.length
              ? '镜头描述已由 AI 细化过（与提示词共用同一份）；直接在这里改就变成你的版本，点「恢复自动生成」可以退回模板原版。'
              : `按「${paneTitle('content')}」页的内容与场景 + 本页参数自动生成；点「AI 细化镜头」能把每段画面写到可拍摄，直接在这里改就变成你的版本。`
      }
      right={bare ? undefined : sbPromptBadge}
    >
      <Textarea
        rows={rows}
        value={sbPrompt}
        onChange={(e) =>
          /* 手改的**同时**记下它是为哪组输入写的 —— 这一格就是「跟着产品走」的全部实现。
             不记的话这份文字会永久压住自动版（2026-10-05 报障）。 */
          patch({ storyboardPrompt: e.target.value, sbInputKey: sbInputKeyNow })
        }
      />
      {/* 折进折叠区时动作不跟着进去 —— 出图后它们由 `stepStoryboard` 渲染在折叠区外 */}
      {bare ? null : sbPromptActions}
    </Field>
  )

  /**
   * 「分镜参考图」= 先用生图模型把 9 个镜头画出来核对，再花视频的钱。
   *
   * 为什么要单独一屏、而不是并进「参数与生成」：用户原话是
   * 「输出产品跟要求，第二步输出分镜图（9 宫格），最后再生成视频」——
   * 它是一次**独立产出**（花一次生图的钱、等一次出图、不满意会重出），
   * 和「选平台/比例/时长」那种改完立刻生效的参数不是一类东西。
   *
   * 这一步的经济意义：视频按秒计价，一条 15 秒 ¥4.50 上下，出来才发现镜头
   * 不对只能整支重跑；先花 ¥0.10 看一眼镜头顺序与构图，是这条链路上最便宜的
   * 一次纠错机会。所以按钮、进度、出图、重出都留在这里，不塞进参数堆。
   *
   * 出图后**不在这里重复列那 9 格文字** —— 右栏那张「分镜脚本」卡本来就常驻，
   * 写的是同一份 `effectiveStoryboard`；两处都列就是同一件事说两遍，
   * 而且改时长时两处都可能漏改一处。
   */
  const stepStoryboard = (
    <Card>
      <CardHead
        title="分镜参考图"
        icon="film"
        hint="出一张 3×3 九宫格核对镜头；生成视频时作为参考图带上（占 1 张额度）"
      />
      <CardBody>
        {sbRunning ? (
          <div className="stack stack--sm">
            <Progress value={sbRunning.progress} indeterminate={sbRunning.progress <= 0} />
            <div className="text-xs muted">正在出分镜参考图，约半分钟。</div>
          </div>
        ) : sbUrl ? (
          /*
            预览在上、提示词区在下（2026-09-30 用户要求「放到下面」）。

            此前是左右并排（并排的理由只是把按钮顶回首屏）。改成上下之后，
            **提示词编辑区默认收起** —— 收起态只多一行折叠头，整体高度与并排时
            基本持平，按钮不至于掉出首屏；要改提示词点那行展开即可。

            折起来的**只有那段文字**。动作一律留在折叠区外常驻：
            「AI 细化镜头」+「复制提示词」+「恢复自动生成」（`sbPromptActions`）、
            「重新生成」、以及出图状态说明。
            判据是「折起来用户就找不到的东西不能折」——
            「AI 细化镜头」恰恰是**出图之后最该被看见的那颗**（2026-10-05：
            用户出完图想问「为什么视频脚本不能用 AI 再生成一次」，
            而它当时正躺在收起态的折叠区里，DOM 都没有）。
          */
          <div className="sbresult">
            {/*
              分镜九宫格**点开放大**（2026-10-08 用户原话：「可以点开」）。

              为什么非点开不可：这张图在这块区域里只有 240px 宽，九宫格摊下来
              每格 ≈80px —— 「第 4 格的手有没有挡住产品」「第 7 格的字是不是歪的」
              这类要看的东西，在缩略图上根本判断不了，而核对镜头恰恰是这一步
              存在的**唯一理由**。

              放大走结果区那份 `ImageLightbox`（点背景 / Esc 关闭、锁背景滚动、
              portal 到 body 避开卡片 `overflow: hidden` 全都在它一份实现里），
              不自己再拼一个遮罩 —— 本项目「交互控件只许一份实现」。
            */}
            <button
              type="button"
              className="sbpreview"
              onClick={() => setSbZoom(true)}
              title="点开放大，逐格核对九个镜头"
            >
              <SmartImage src={sbUrl} alt="九宫格分镜参考图" />
              <span className="sbpreview__hint">
                <Icon name="expand" size={12} />
                点开放大
              </span>
            </button>
            <div className="sbresult__side">
              {/* 折叠头一律用 `CardHead`（整块可点 / aria-expanded / 键盘可达 / chevron
                  只有那一份实现，smoke-nav 会拦住自己拼 summary 的写法）；
                  容器只负责把它的默认 padding 收掉，见 global.css 的 `.inlinefold` */}
              <div className="sbfold inlinefold">
                <CardHead
                  title="分镜提示词"
                  open={sbPromptOpen}
                  onToggle={() => setSbPromptOpen((v) => !v)}
                  right={sbPromptBadge}
                />
                {sbPromptOpen ? sbPromptField(6, true) : null}
              </div>
              {/* 三颗动作：改提示词 / 让 AI 写镜头脚本 / 复制 —— **不随折叠收起** */}
              {sbPromptActions}
              {/* 把「这颗按钮改的是哪份东西」说透：用户看的是生成视频时那段【镜头脚本】，
                  而按钮长在「分镜提示词」旁边 —— 不点明就没人知道它俩是同一份数据
                  （2026-10-05 用户原话：「为什么视频脚本不可以用 ai 生成多一次」）。
                  ⚠️ 这里不能写 `**`：本项目没有 markdown 渲染器，星号会原样显示出来。 */}
              <div className="text-xs muted">
                「AI 细化镜头」写的就是右栏那张「分镜脚本」卡 —— 它和生成视频时提示词里的
                【镜头脚本】是同一份数据，改一次两处一起变。
              </div>
              <div className="text-xs muted">
                {/* 出图时间不是装饰：这张图可能来自上一次会话（判据是提示词逐字一致，
                    不是"刚刚生成"），不写时间用户会以为它是刚出的、甚至以为是别人的图。 */}
                {sbDone ? `${fmtTime(sbDone.createdAt)} 出图 · ` : ''}
                {refPlan.storyboard
                  ? '已作为参考图带上：生成视频时会随产品图 / 场景图一起提交。'
                  : `参考图额度已满（本模型共 ${refImageMax} 张），生成视频时不会带上它 —— 想带就在「${paneTitle('source')}」「${paneTitle('content')}」两页少留几张。`}
              </div>
              {/*
                两颗动作并排：重出分镜图 / 把这一整套存成模板。

                「存为模板」刻意放在**看到这张九宫格之后**：用户口径是
                「如果保存这个过程，可以抽结构出来存储到模板中心」——
                他得先看到分镜与成片，才知道这套值不值得存。
                封面直接取手上这张九宫格（后端会转存，见 `saveTemplate`）。
              */}
              <div className="row">
                <Button variant="ghost" size="sm" icon="refresh" onClick={doStoryboard}>
                  按这份提示词重新生成
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  icon="star"
                  onClick={openSaveTemplate}
                  title="把当前这套参数、分镜脚本与全片约束存成自己的模板；封面用这张九宫格，内容带上本页最近这条成片"
                >
                  存为模板
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs muted" style={{ marginBottom: 10 }}>
              两步：先按「{paneTitle('content')}」页的产品名称 / 场景 / 卖点（连同右栏那份镜头脚本）自动写一份提示词，
              你可以直接改；再用它出一张 3×3 九宫格 —— 每格是这支片子的一个镜头，从左上到右下按时间走。
              这一张是给人核对的，不满意可以重出。
            </div>
            {sbPromptField(10)}
            <div style={{ marginTop: 12 }}>
              <Button icon="sparkles" onClick={doStoryboard}>
                生成分镜参考图
                {sbPrice?.estimatedPrice ? ` · ¥${sbPrice.estimatedPrice.toFixed(2)}` : ''}
              </Button>
            </div>
          </>
        )}
        {/*
          两种「手上那张不算数」要**分开说** —— 合起来说一句「已失效」，
          用户就分不清是「参数变了、要重出」还是「已经用掉了、本来就该没」：
            · `sbUsed`＝已经随「生成视频」交出去，按用户要求清空（2026-10-05）；
            · `sbStaleUrl`＝上一版提示词画的（参数变过）。

          两者都**不渲染预览、不进参考图**，只提一句。用户报障原话就是
          「这张怎么一直在的」：它明明与当前参数无关，却占着预览位、还被当成「已带上」。
        */}
        {sbUsed ? (
          <div className="text-xs muted" style={{ marginTop: 12 }}>
            上一张分镜图已经随「生成视频」用掉，按你的要求清空了 —— 不再显示，也不会再带进下一条片子。
            要给下一条片子核对镜头，点上面的按钮重出一张。
          </div>
        ) : null}
        {sbStaleUrl ? (
          <div className="text-xs muted" style={{ marginTop: 12 }}>
            上一次出的那张分镜图是按改动前的提示词画的，已经不算数：不显示大图，也不作为参考图带上。
            点上面的按钮就会按现在这份提示词重出一张。
          </div>
        ) : null}
        {sbFailed ? (
          <div className="text-xs" style={{ marginTop: 10, color: 'hsl(var(--destructive))' }}>
            上一次出图失败：{sbFailed.error?.message ?? '未知原因'}。可以再点一次重出。
          </div>
        ) : null}
      </CardBody>
    </Card>
  )

  /* ------- 页签 · 输出参数（合并页第 1 位；复刻档第 2 位，见 PANES 头注释） ------- */

  const stepParams = (
    <>
      <Card>
        <CardHead
          title="输出参数"
          icon="bolt"
          /* 报价只在最后一个页签出现（见 submitDock 的注释），所以这里不能再写「改完报价立刻变」——
             它改的是这一页，报价却在右栏顶部（且要走到最后一步才有），写「当场看见」就是撒谎。
             ⚠️ 「最后一步」的名字**必须从 `tabs` 取**：复刻档最后一页是「内容与场景」，
             合并页才是「分镜参考图」—— 写死一个名字，总有一个档的文案是错的。 */
          hint={`这一页的每一项都直接决定花费；报价在最后一步（${tabs[tabs.length - 1]?.title ?? ''}）出现在右栏顶部`}
        />
        <CardBody>
          {/* 短控件两两并排：并排一对省约 90px 纵向高度 */}
          <div className="field-row">
            <Field label="投放平台" hint={`${platform.name} · 通版 ${platform.sizes.video}`}>
              <Select value={form.platform} onChange={(e) => patch({ platform: e.target.value })}>
                <optgroup label="国内">
                  {DOMESTIC_PLATFORMS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="海外">
                  {OVERSEAS_PLATFORMS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </optgroup>
              </Select>
            </Field>

            <Field label="视觉风格">
              <Select value={form.style} onChange={(e) => patch({ style: e.target.value })}>
                {VIDEO_STYLES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="field-row">
            <Field
              label="清晰度"
              hint={`${modelInfo?.name ?? '当前模型'}支持 ${resOptions.map(resolutionLabel).join(' / ')}`}
            >
              <Segmented
                value={form.resolution}
                onChange={(v) => patch({ resolution: v })}
                /*
                  ⚠️ **value 用模型的原值，label 才归一化**：
                  上游各模型大小写不统一（同一个平台里 720P 与 720p 并存），
                  直接渲染会在同一排里出现两种长相；但提交必须是模型认的那个原值，
                  改大小写会被 `_guard_value()` 判成非法值。
                */
                options={resOptions.map((r) => ({ value: r, label: resolutionLabel(r) }))}
              />
            </Field>

            <Field label="画面比例" hint="只列当前模型能出的比例">
              {/*
                2026-10-08 由 `Segmented`（一排按钮）改成下拉。

                它是这一页**选项最多**的控件：实测 9 个（sd 系更多，21:9 / 3:2 / 2:3），
                一排横过去占掉小半屏，也是视频工作台首屏「58 个可点元素」里
                单一来源最大的一处（9 个）。改成下拉后首屏少 8 个可点元素。

                ⚠️ 清晰度（4 个）与运镜（3 个）**保持分段控件**，不是漏了：
                选项少时「一眼看全 + 一点就中」比下拉快。这条界线是**选项数**，
                不是「统一风格」—— 把三个都改成下拉，是把好用的那两个也降级。

                ⚠️ 选项文字**只用比例原值**，不套 `ratioLabel()`：
                那个函数的用途说明是**图片语境**的（`16:9 · 横幅 · A+ 宽图 · 视频封面`），
                贴在视频工作台上会把「横版视频」说成 A+ 图。用途改由上面那句
                `hint` 交代（它还必须说清「选项数会随模型变」，否则用户换模型后
                看到比例少了两项会以为功能丢了）。
              */}
              <Select
                value={form.aspectRatio}
                onChange={(e) => patch({ aspectRatio: e.target.value })}
              >
                {/* 只渲染**本模型允许**的比例：14 个模型的比例集不完全一样
                    （渠道系多一个 adaptive，sd 系多 21:9 / 3:2 / 2:3） */}
                {ratioOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="运镜">
              <Segmented
                value={form.camera}
                onChange={(v) => patch({ camera: v })}
                options={VIDEO_CAMERAS.map((s) => ({ value: s, label: s }))}
              />
            </Field>
          </div>

          {/* 时长单独一行且带价格提示 —— 它是本页唯一「一改就变价」的控件 */}
          <Field label="时长">
            <Segmented
              value={String(form.duration)}
              onChange={(v) => patch({ duration: Number(v) })}
              options={VIDEO_DURATIONS.map((n) => ({ value: String(n), label: `${n}s` }))}
            />
          </Field>
        </CardBody>
      </Card>
    </>
  )

  /* 页签清单 `tabs` 定义在组件上方（原来的位置在这儿）—— 提交栏的 `isLastStep`
     与正文取用都读那一份，**不许在这里再算一次「复刻档少一个」**：
     同一件事算两遍，迟早出现「页签栏 3 个、提交栏以为有 4 个」。 */

  /* ------------------- 提交栏（只在最后一个页签出现） ------------------- */

  /**
   * 挂进**右栏最顶上**（`panel` 里排在 `<StudioPanel>` 之前、`.studio__dock` 吸顶），
   * 但**只在最后一个页签挂上去** —— 排在它前面的页签整条不存在（不是隐藏，是不渲染）。
   * 合并页是前 3 个，复刻档是前 2 个 —— 所以判据只能写「不是最后一个」，不能写数字。
   *
   * ⚠️ 2026-10-08 全站统一（其余 8 页的提交栏也在这个位置）时，本页这条
   * **刻意保留**：它是 2026-09-30 用户单独为本页定的口径，与「位置」无关。
   *
   * 2026-09-30 用户口径：「这个（报价 + 生成视频）在最后一个才出现」，
   * 并明确范围「只改视频这个」（其余 7 个生成页维持吸底常驻）。
   *
   * 这一条改掉了 2026-09-28 定的「页签可以切，报价必须一直在」——
   * 那条是**三步向导**语境下的结论（当时的毛病是「走完流程才知道要花多少钱」）。
   * 视频页现在是「先出分镜参考图、最后再生成视频」，报价与按钮从第 1 步就挂在
   * 眼皮底下，等于把「最后才生成」这条流程意图又抹掉了；而且它吸底会盖住
   * 当时第 1 页的产品参考图区。
   *
   * ⚠️ 判据取 `tabs.length - 1` 而**不是** `PANES.length - 1`：复刻档只有 3 个
   * 页签（分镜参考图被过滤掉），它的最后一个是「内容与场景」，按 `PANES` 算会差一位。
   * 用 `>=` 而不是 `===`：万一 `step` 越界（旧 localStorage 里存着大值），
   * 失败方向应该是**仍然看得见提交栏**，而不是整页找不到按钮。
   *
   * 已知代价（用户已确认接受）：最后一个页签之前看不到价格。
   * 2026-09-30 页签重排后这条代价落在「输出参数」页上 —— 那一页恰恰是改价格的页，
   * 所以它的卡头 hint 里写明了「最终报价在最后一步时出现在右栏顶部」，不装作当场能看见。
   * `onReset` 顺带把页签拉回第 1 页。
   */
  const isLastStep = step >= tabs.length - 1

  const submitDock = isLastStep ? (
    <Card className="studio__dock">
      <CardBody tight>
        <SubmitBar
          price={price?.estimatedPrice ?? 0}
          basis={price?.basis}
          free={price?.isFreeThisCall}
          disabled={!ready}
          disabledReason={!ready ? (needVideo ? '请先上传参考视频' : '请先填写产品名称') : undefined}
          label="生成视频"
          onGenerate={doGenerate}
        />
      </CardBody>
    </Card>
  ) : null

  /**
   * 页签 id → 正文。**键集合必须与 `PANES` 的 id 集合逐项相等**（冒烟钉着这条，
   * 见 smoke-nav 的「生成页骨架」段）—— 少一个键，点过去就是一片空白。
   *
   * ⚠️ 这里是 **`PANES` 的 id 集合**，不是「某一档实际渲染的那几个」：
   * `analyze` 只在复刻档出现、`storyboard` 只在合并页出现，但两个键都必须留着 ——
   * 缺一个，冒烟就报「声明了 5 个页签却有 4 个键」，而那其实是**对的**。
   * 判据是「PANES 里每一页都有正文可渲染」，与某一档用不用它无关。
   *
   * 2026-10-08 由「按下标取数组」改成「按 id 取映射」。两档顺序不同之后，
   * `[stepParams, stepSource, …][step]` 就成了一个**静默错位源**：顺序一改，
   * 第 1 个页签会渲染成第 2 页的正文 —— 页签栏亮着、内容却是别人的，
   * 编译期与类型检查全看不出来（本项目在页签上已经踩过一次同类）。
   * 改成按 id 取之后，**页签怎么排、正文就跟着怎么排**，两者不可能脱钩。
   *
   * ⚠️ 索引写 `tabs.length - 1` 而**不是** `PANES.length - 1`：两档的页签数都比
   * `PANES` 少（合并页 4 / 5、复刻档 4 / 5），按 `PANES` 算时，老 localStorage 里
   * 存着的 `step=4` 会取到 `tabs` 里不存在的项。
   * 末尾 `?? stepParams` 是同一条兜底 —— 失败方向是「回到第 1 页的正文」，
   * 而不是「页签栏亮着、正文一片空白」。
   */
  const PANE_BODIES: Record<string, ReactNode> = {
    params: stepParams,
    source: stepSource,
    /* 复刻档专属：`source` + `content` 的正文**原样拼起来**，不再另写一份 ——
       两页各自的正文仍要留着（合并页还在用它们），所以合并页只能拼、不能改它们。
       顺序 = 原页签顺序（先引擎与产品，后内容与场景）。 */
    engine: (
      <>
        {stepSource}
        {stepContent}
      </>
    ),
    /* 复刻档专属（第 1 页）。合并页不出现这一页，但键要留着，理由见上 */
    analyze: stepAnalyze,
    content: stepContent,
    /* 合并页专属（第 4 页）。复刻档不出现这一页（它复刻的是既有结构，
       没有分镜可排），同样只留键不给顺序表。 */
    storyboard: stepStoryboard,
  }
  const stepBody = PANE_BODIES[tabs[Math.min(step, tabs.length - 1)].id] ?? stepParams

  return (
    <div className="page page--wide">
      {/*
        档位选择留在页签外：它决定整条工作流（场景清单 / 时长 / 比例 / 默认平台），
        而且**换档 = 换一整批参数**（见 switchMode），不属于任何一个页签里的参数。
        合并前这是两条侧栏入口（带货短视频 / 商品视频）—— 同一批决策要用户自己
        想清楚去哪个页面，进来还得重新选一遍平台与时长。
        视频复刻没有这一段：它是独立页，输入物相反（必传参考视频）。
      */}
      {/*
        档位 chip-row —— **2026-10-05 起不再出现**：`short-video` 与 `main-video`
        并成了一档，`STUDIO_VIDEO_MODES` 只剩一条。判据写成「由清单长度派生」
        而不是直接删掉这段：一个 chip 的 chip-row 没有意义，但清单一旦重新分档，
        这段自动回来，页面不用改。同源判据见 config/videoModes.ts 头部说明。
      */}
      {!isReplicate && STUDIO_VIDEO_MODES.length > 1 ? (
        <Chips
          className="chip-row"
          style={{ marginBottom: 12 }}
          value={mode}
          onChange={switchMode}
          options={STUDIO_VIDEO_MODES.map((m) => ({ value: m.id, label: m.name }))}
          titles={Object.fromEntries(STUDIO_VIDEO_MODES.map((m) => [m.id, m.desc]))}
        />
      ) : null}

      {/*
        复刻方式 —— 复刻档的**根决策**，与上面那排档位 chip-row 同一格位置。

        2026-10-08 从第 4 页「内容与场景」里搬出来。搬之前它长在那一页的
        「视频场景」控件里（`meta.scenes` 复用成了两个复刻方式），用户口径：
        「`/video-replicate` 复刻这个也是，这个搞笑吧」。

        为什么「放最后一步」是错的（不只是难看）：
          · 它决定的是**整条片子怎么来的** —— 结构复刻照搬镜头顺序与切换时机，
            节奏复刻只借快慢与镜头密度。第 1 页的「分析视频」拆出的镜头结构、
            第 3 页报的价、第 4 页会发出去的提示词，全都受它影响；
          · 一个决定后面三页怎么走的选择，摆在最后一页 = 用户填完三页才看见它，
            而那时他已经按「结构复刻」把参考视频分析过了。
        铁律：**根决策放页签外的 chip-row**（与档位、模板行同一层）。
        同一批东西在「内容与场景」里不再渲染第二遍 —— 同一字段两个入口是禁止的。
      */}
      {isReplicate ? (
        <div className="chip-row" style={{ marginBottom: 12, alignItems: 'center' }}>
          <span className="text-xs muted">复刻方式</span>
          <Chips
            value={form.scene}
            onChange={pickScene}
            options={meta.scenes.map((s) => ({ value: s, label: s }))}
            titles={{
              结构复刻: '照参考视频的镜头顺序、机位与切换时机走 —— 换掉产品与画面内容，结构照旧',
              节奏复刻: '只借参考视频的快慢、镜头密度与切换节奏，每个镜头拍什么由这支片子自己定',
            }}
          />
        </div>
      ) : null}

      {/*
        模板行与档位 chip-row 同为**根决策**，都在页签外，且都在左栏上方。
        视频复刻不渲染它：那一档的输入物是参考视频（复刻既有结构），
        「场景 / 风格 / 时长」这套预设对复刻没有意义。
      */}
      {!isReplicate ? (
        <TemplateRail
          mode={mode}
          custom={customTpls}
          current={tpl}
          onPick={pickTemplate}
          onPreview={setPreviewTpl}
          /* 与分镜页那颗「存为模板」是**同一个入口**（都调 `openSaveTemplate`）——
             两处各写一遍的话，迟早一处带封面、另一处忘带。 */
          onSave={openSaveTemplate}
        />
      ) : null}

      <GeneratorFrame
        form={
          <WorkflowTabs tabs={tabs} current={step} onChange={setStep}>
            {stepBody}
          </WorkflowTabs>
        }
        panel={
          <>
            {submitDock}
            <StudioPanel
              jobs={jobs}
              outputs={outputs}
              onCancel={cancel}
              onClear={clearResults}
              activeHint="视频生成较慢，任务在后台跑，切页不中断"
              resultTitle={`${meta.name} 结果`}
              /* 标题跟着当前档走。注意两栏的**取数**已经不是「按当前档收窄」了 ——
                 并档后 `slugs` 一次带上 short-video 与 main-video 两个 slug（见上面那段注释），
                 所以这里说的是「这一栏归哪一档管」，不是「只显示哪一档的记录」。 */
              historyTitle={`${meta.name} 历史`}
              resultEmpty="确认参数后点「生成视频」。完成后结果会在这里和「我的作品」同时出现。"
              aspectRatio={form.aspectRatio}
              historyTabs={historyTabs}
              /*
                成片提示词 —— 右栏**顶部**常驻的那张卡（2026-10-08 从底部那张
                「分镜脚本」卡升上来的）。

                一次改掉三件事，都是同一个报障引出来的（用户口径「分镜脚本显示有点
                猥琐」，复刻页那句是「这个搞笑吧」「应该有个 AI 按钮生成提示词，
                因为前面参数需要汇总」）：

                  ① **位置**：原先挂在 `children`（整栏最底部）。1440×900 下量到
                     y=984 —— 首屏 1000px 装不下，要滚动才看得见。现在走 `head`
                     插槽，落在「进行中任务」条之后、历史卡之前。
                  ② **内容**：复刻档下它显示的是 `GENERIC_BEATS` 那段通用分镜
                     （「产品定格特写，快速推进制造视觉钩子」）—— 而提示词那边早就
                     写着「有参考视频时不给通用分镜」（见 `buildVideoPrompt` 的
                     `shotList`）。卡片与真源对不上，还自称「与提示词是同一份数据」。
                     现在复刻档显示的是**参考视频复刻**这件事本身。
                  ③ **能力**：卡头加一颗「AI 生成提示词」，把前面几页的参数汇总成
                     一版成片提示词（`doAiPrompt`）。它只是草稿，不参与提交。

                ⚠️ 卡里**不挂** `buildVideoPrompt` 那份原文。那是既有约定：
                提示词的唯一展示处是「生成视频」的确认框（见 `components/studio.tsx`
                里那段注释），页面上再挂一份就是同一件事两个出口。这里给的
                `aiPrompt` 是**另一件产物** —— AI 按参数重写的一版草稿。
              */
              head={
                <Card>
                  <CardHead
                    title="成片提示词"
                    icon="text"
                    hint={`${form.duration} 秒 · ${form.scene} · ${form.camera}`}
                    right={
                      <Button
                        variant="outline"
                        size="sm"
                        icon="sparkles"
                        loading={aiPromptBusy}
                        /* 产品名是唯一的硬前置：上下文里没有产品，AI 只能编一个
                           （同 `/banner` 那条）。不禁用「没卖点」—— 那一格本来就可选。 */
                        disabled={!form.productName.trim()}
                        title={
                          form.productName.trim()
                            ? '把这支片子的参数（参考视频分析 / 引擎与产品 / 输出参数 / 内容与场景）汇总成一版成片提示词（按次计费）'
                            : '先填产品名称 —— 没有它，AI 只能编一个产品'
                        }
                        onClick={() => void doAiPrompt()}
                      >
                        AI 生成提示词
                      </Button>
                    }
                  />
                  <CardBody tight>
                    {aiPrompt ? (
                      <>
                        <div
                          className="row"
                          style={{ justifyContent: 'flex-end', gap: 6, marginBottom: 6 }}
                        >
                          <CopyButton text={aiPrompt} label="复制这版" />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon="refresh"
                            title="清掉这版草稿，回到分镜脚本"
                            onClick={() => setAiPrompt(null)}
                          >
                            恢复
                          </Button>
                        </div>
                        {/* 复用全站那份提示词文本块样式（pre-wrap + 260px 内滚动 +
                            同款边框），与批量行、任务抽屉里看到的是同一个样子 */}
                        <pre className="task__textbody">{aiPrompt}</pre>
                        <div className="text-xs muted" style={{ marginTop: 10 }}>
                          这是 AI 按当前这几页的参数汇总出来的<b>草稿</b> —— 不会自动提交。
                          真正发出去的那份在「生成视频」的确认框里逐字展示。
                        </div>
                      </>
                    ) : isReplicate ? (
                      /*
                        复刻档**不显示通用分镜**。那三段（「产品定格特写…」）是
                        `GENERIC_BEATS`，属于**别的片子**的结构 —— 而提示词那边早就写着
                        「有参考视频时不给通用分镜」（见 `buildVideoPrompt` 的 `shotList`：
                        它跟参考视频抢方向盘，而且更具体、多半会赢）。
                        卡片显示它，用户就会拿它当成这支片子的镜头，与真源对不上。

                        换成「本片会按什么走」这一句实话：有分析结果就说拆出了几个镜头，
                        没有就指路去第 1 步。**逐镜头清单不在这里铺** —— 它在
                        「{paneTitle('analyze')}」页那张卡里可编辑，这里再列一遍就是
                        同一个东西两个展示处（而且这一栏只有 300~380px 宽）。
                      */
                      <div className="text-sm">
                        {analysisShotCount > 0 ? (
                          <>
                            本片按参考视频复刻 —— 已拆出 <b>{analysisShotCount}</b> 个镜头，
                            顺序、机位与切换时机全照它执行。逐镜头清单在「{paneTitle('analyze')}」页。
                          </>
                        ) : (
                          <>
                            本片按参考视频复刻 —— 还没分析参考视频。先到「{paneTitle('analyze')}」页
                            传片子并点「AI 分析」，拆出来的镜头结构会直接进提示词。
                          </>
                        )}
                        <div className="text-xs muted" style={{ marginTop: 10 }}>
                          想看整份提示词，点右上角「AI 生成提示词」汇总一版，或到「生成视频」的确认框。
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="storyboard">
                          {storyboard.map((s) => (
                            <div className="storyboard__item" key={s.t}>
                              <span className="storyboard__t">{s.t}</span>
                              <span className="grow">{s.d}</span>
                            </div>
                          ))}
                        </div>
                        <div className="text-xs muted" style={{ marginTop: 10 }}>
                          与提示词里的【镜头脚本】是同一份，改时长两边同时变。
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>
              }
            />
          </>
        }
      />

      <PriceConfirmDialog
        info={flow.pending}
        onCancel={flow.cancel}
        onConfirm={(edits) => {
          /*
           * ⚠️ **`edits` 必须原样透传给 `flow.confirm`**（2026-10-08 加）。
           * 本页是全站唯一把 `onConfirm` 包了一层的调用方；包一层之后参数就断了 ——
           * 而确认弹窗里那段提示词现在是**可编辑**的（`<Textarea data-testid="base-prompt">`），
           * 丢掉 `edits` 的表现是：用户改完点确认，提交成功、图照出、钱照扣，
           * 只有内容不是他改过的那一版。这类「预览与实发不是同一份」
           * 在花钱按钮前面最不该破。
           */
          /*
           * 「分镜图片使用过就清空」（2026-10-05 用户要求）。
           *
           * 只在**这一次确认的是出成片、且分镜图真的进了上送清单**时才记：
           * 出分镜图本身那次提交不消耗任何东西（它的 `appSlug` 是 `STORYBOARD_SLUG`），
           * 而额度不够时 `refPlan.storyboard` 本来就是 false（没带上，自然不算用过）。
           * 记在「确定要花这笔钱」的这一刻而不是等任务跑完：用户点了确认就是决定要用它了；
           * 万一任务失败，重出一张分镜图也只要 ¥0.10。
           */
          const usedUrl = flow.pending?.appSlug === STORYBOARD_SLUG ? '' : sbUrl
          void flow.confirm(edits)
          if (usedUrl) patch({ sbUsedUrl: usedUrl })
        }}
      />

      {/*
        模板预览抽屉。挂在页面根节点、不放进卡片 —— 卡片是 `overflow: hidden`，
        弹层万一被裁就是「点了没反应」。它只在点了卡片上那颗播放按钮时才有内容。
      */}
      <TemplatePreview
        tpl={previewTpl}
        onClose={() => setPreviewTpl(null)}
        onUse={(t) => {
          pickTemplate(t)
          setPreviewTpl(null)
        }}
      />

      {/*
        分镜九宫格的放大遮罩（2026-10-08 用户口径：「可以点开」）。

        ⚠️ 只有一张图也要走 `ImageLightbox` 而不是自己写个 `<img>` 铺满屏：
        点背景关闭、Esc 关闭、锁背景滚动、portal 到 body（卡片 `overflow: hidden`
        会把遮罩裁掉）这四件事在任何图片查看器里都是同一套，本项目里它们
        只有这一份实现。`index` 恒为 0、`onIndex` 给空实现 ——
        单图没有「上一张 / 下一张」，`total === 1` 时那两个箭头本来就不渲染。
      */}
      {sbZoom ? (
        <ImageLightbox
          items={[{ url: sbUrl, type: 'image', thumbUrl: sbUrl, text: '九宫格分镜参考图' }]}
          index={0}
          onIndex={() => {}}
          onClose={() => setSbZoom(false)}
          title="分镜参考图"
        />
      ) : null}

      {/*
        「存为模板」弹窗。**它只收集名字 / 角标 / 说明三件事** ——
        参数、分镜、封面、成片样例全在这里抽好交进去（见 `saveTemplate`）。
        让弹窗自己去读表单是错的：用户完全可以开着弹窗去改参数（同一屏就能改），
        那样「存的是哪一套」就取决于弹窗挂载那一刻表单长什么样。

        也挂在页面根节点：卡片是 `overflow: hidden`，弹层放进去会被裁。
      */}
      <SaveTemplateDialog
        open={saveTplOpen}
        onClose={() => setSaveTplOpen(false)}
        /* 产品名优先 —— 用户在模板条里认卡片，多半是按「这是那个榨汁杯的」认的。
           没填产品名才退回当前模板名（此时他必然是点了某张模板铺开来的）。 */
        initial={{
          name: form.productName.trim() || (tpl ? templateTitle(tpl) : ''),
          /* 角标留空：工作台的模板卡只有 84px 宽，「我的模板 · 」这 7 个字是白吃的 */
          tag: '',
          description: '',
          /* 模板内容两块的初值 = **当前这套实际会发出去的那一份**
             （`storyboard` 是叠好镜头细化的结果，`tpl.shot` 是当前命中模板的全片约束）。
             不空着让用户从零写：多数情况下他要的只是把其中两条改得更具体，
             给一份空的等于先把手上有的东西扔掉再让他重写一遍。 */
          shots: storyboard.map((s) => ({ t: s.t, d: s.d })),
          shot: tpl?.shot ?? '',
        }}
        coverUrl={sbUrl}
        detail={{
          summary: `${form.scene} · ${form.style} · ${form.camera} · ${form.duration}s · ${form.aspectRatio}`,
          hasSample: !!sampleVideoUrl,
        }}
        onSubmit={saveTemplate}
        /* 两块内容各一颗 AI 扩写。只有工作台这两处能传 ——
           模板中心那页没有「这支片子拍什么」的上下文（见组件的 `onAiShots` 注释） */
        onAiShots={aiExpandShots}
        onAiShot={aiExpandShot}
      />
    </div>
  )
}
